/**
 * Clerk console (M5). Two screens on one page:
 *   - the online clerk (buttons): lot head, bid board, increments, floor /
 *     fair warning / hammer / sold / pass, pause net, catalog rail, bidders;
 *   - the paper clerk (keyboard strip): amount → Tab → paddle → Enter, from
 *     keyboard.ts, always live when focus is not in a text field.
 *
 * Everything the console KNOWS about the sale comes from control-api
 * (catalog, registrations/paddles); everything that HAPPENS comes from the
 * ring over the protocol. The console holds no authority of its own: a
 * rejected command is shown, never retried silently.
 */

import { ControlApiClient, ApiError, type Identity } from '@hipnumber/api-contract/client';
import { pickRing, ringAddresses, type RingAddress, type RingAddresses } from '@hipnumber/api-contract/ring-addresses';
import QRCode from 'qrcode';
import { ProtocolClient, type InterestFrame, type MaxBidsFrame, type Snapshot } from '@hipnumber/protocol-client';
import { answerLine, equalBidOffer, highBidFromSnapshot, withExpected, DEGRADED_LINE, notAssignedView, bidCorrectionOffer, SOURCE_WORDS } from './ring-guard.js';
import { closeStepAction, closeStepWords } from './ring-guard.js';
import { askAfterStep, forgetStep, incrementChoices, incrementView, parseSavedSteps, parseStep, rememberStep, removeFromRingOffer, type IncrementView } from './ring-guard.js';
import { bidLimitCents, holdsHighBid } from './floor-ops.js';
import { bootSession, NeedsSignIn, type Session } from './session.js';
import { IDLE, keyDisplay, keyReducer, type KeyContext, type KeyState } from './keyboard.js';
import { findByPaddle, pricingView, reopenAllowed, reopenTarget, reopenableOf, incrementAt, openingAskFor, nextInRunOrder, findBidders, phoneTail, saleWarnings, splitTitleFor as splitTitle } from './floor-ops.js';
import { el, setChildren } from './dom.js';
import { initTheme } from './theme.js';
import { mountModeSwitcher } from './mode-switcher.js';
import { modeByKey } from './work-modes.js';
import { RingHistory, bidMarks, bidRows, bidSourceWord, bidViaWords, bidderWords, historyNote, paceCells, parseActivityReply, type ActivityReply } from './ring-history.js';
import { maxBidLine, maxBidViewedFeed, maxBidsView } from './max-bids.js';
import { interestView } from './interest.js';
import { SaleChecks, checksView, parseSaleHealth, type ChecksLine } from './sale-checks.js';
import { saleWords, setSaleWords } from './lot-term.js';
import { openLotDialog } from './lot-dialog.js';
import { addWalkInLot } from './walk-in-lot.js';
import type { Seller } from './seller-search.js';

/** ADR-27 lot kinds. The server re-validates on createLot (unknown kind →
 *  422); this is the client-side nicety for the add-lot form. */
const KINDS = ['equine', 'tack', 'cattle', 'exotic', 'trailer', 'notice', 'general'];
/** The kind of the last lot added at this console, so the next walk-in starts on it. */
let lastLotKind = 'equine';

/** The tenant's sellers for the add-lot dialog's picker. Asked each time the
 *  dialog opens: a seller registered at Horse Check-In a minute ago must be
 *  pickable, and one small GET at a ring is cheap. */
async function loadSellerList(): Promise<Seller[]> {
  return (await api.call('listConsignors', { identity: session.apiIdentity() })) as unknown as Seller[];
}

type LotMedia =
  | { type: 'image'; variants: Record<string, { url: string; width: number; height: number }> }
  | { type: 'video'; url: string | null };
type Lot = { id: string; hipNumber: string; runOrder: number | null; kind: string; title: string; status: string; pricingMode: string; quantity: number; openingAskCents: number | null; reserveCents: number | null; attributes: Record<string, unknown>; descriptionMd: string | null; consignorName?: string | null; media?: LotMedia[]; ring?: { ringId: string; name: string } | null };

/**
 * The smallest image that is still big enough, else the biggest there is.
 *
 * A clerk rail is 46px and the hero is ~150px: pulling a 2000px original into
 * either is a slow ring on a venue's wifi. Variant names are not guaranteed,
 * so this sorts by actual width rather than trusting a 'thumb' key to exist.
 */
function imageUrl(lot: Lot | undefined, minWidth: number): string | null {
  const img = lot?.media?.find((m): m is Extract<LotMedia, { type: 'image' }> => m.type === 'image');
  if (!img) return null;
  const sizes = Object.values(img.variants ?? {}).filter((v) => v && typeof v.url === 'string');
  if (sizes.length === 0) return null;
  const up = sizes.filter((v) => (v.width ?? 0) >= minWidth).sort((a, b) => a.width - b.width);
  return (up[0] ?? sizes.sort((a, b) => b.width - a.width)[0])!.url;
}

/** How many videos a lot carries, for the badge. Never auto-played at a ring. */
const videoCount = (lot: Lot | undefined): number => (lot?.media ?? []).filter((m) => m.type === 'video').length;

/** Re-exported from floor-ops so the ring and paper stations split a title
 *  the same way. */
/** Re-exported so existing importers of main.ts keep working. */
export { splitTitleFor as splitTitle } from './floor-ops.js';

/**
 * The rail thumbnail: the horse when there is a photo, the hip tag when there
 * is not.
 *
 * The tag is what renders FIRST, and the photo replaces it only after it has
 * loaded. The URL is wrapped in quotes and any quote stripped, because it
 * arrives from the API and a bare url() would let one break out of the
 * declaration.
 */
function thumb(lot: Lot): HTMLElement {
  const h = el('span', { class: 'h', text: lot.hipNumber });
  const src = imageUrl(lot, 46);
  if (!src) return h;
  // Swap to the photo only once it has ACTUALLY loaded. The first version
  // applied the image class immediately and set the number to transparent,
  // so every lot whose media row pointed at a file that would not load - and
  // in the demo catalog that was most of them - rendered as an empty grey
  // tile. A run order full of blanks is worse than no photos at all.
  const probe = new Image();
  probe.onload = () => {
    h.classList.add('img');
    h.style.backgroundImage = `url("${src.replace(/["\\]/g, '')}")`;
    h.setAttribute('aria-label', `${saleWords().cap} ${lot.hipNumber}`);
  };
  probe.src = src;
  return h;
}
type Sale = { id: string; slug: string; title: string; status: string; product?: string; incrementTable?: unknown; ringProvisioning: Record<string, unknown> };
type Registration = { id: string; accountId: string; onlinePaddle: number | null; livePaddle: number | null; approvalStatus: string; bidLimitCents: number | null; bidderName?: string | null; bidderPhone?: string | null; ringBidderId?: string | null };

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const params = new URLSearchParams(location.search);
const API = params.get('api') ?? (location.hostname === 'localhost' || location.hostname === '127.0.0.1' ? 'http://localhost:4100' : location.origin.replace('console.', 'api.'));
const usd = (n: number | null | undefined): string => (n === null || n === undefined ? '-' : '$' + n.toLocaleString());

// ---- state --------------------------------------------------------------
let session: Session;
let api: ControlApiClient;
let sale: Sale | undefined;
let lots: Lot[] = [];
/** Every lot of the sale; `lots` is this console's ring. Hip numbers are
 *  unique across the whole sale, so a new lot is checked against all of them. */
let saleLots: Lot[] = [];
/** ADR-70 cut 5c: the ring this console sells, and whether the sale has others. */
let myRing: RingAddress | null = null;
let multiRing = false;
/** Every ring of the sale, for their names (cut 5f). */
let saleRings: RingAddress[] = [];
/** ADR-70 cut 5f: the ring said the office gave this clerk another ring. */
let notAssigned = false;
let regs: Registration[] = [];
/** ADR-70 cut 6f: the ring's last Online interest frame (this screen alone),
 *  and the lines already flashed, so a bid flashes once. */
let interest: InterestFrame | null = null;
let interestFlashed = new Set<string>();
let snap: Snapshot | null = null;
let ring: ProtocolClient | undefined;
let keys: KeyState = IDLE;
let lastScrolledTo: HTMLElement | null = null;
let lastFloorBidSeq: number | null = null;
/** Paddle of the current high bid - from the floor channel (the engine's
 *  snapshot does not carry it yet); null for online bids. */
let highPaddle: string | null = null;
/**
 * Whether the standing high bid came off the FLOOR.
 *
 * Deliberately separate from highPaddle, because that is null in two very
 * different situations: an online bid (buyer known by their account) and a
 * floor bid taken from an anonymous raised hand (buyer known by nobody).
 * Conflating them is precisely how a horse gets sold to no one.
 */
let highIsFloor = false;
const feed: string[] = [];
/**
 * ADR-70 cut 6a: what this ring did, lot by lot - each lot's bids with their
 * markers, and the times the pace is made from. Folded from the floor wire,
 * read from the ring's record on the first snapshot and whenever events were
 * missed (ring-history.ts). "Bids this lot" is its count.
 */
const history = new RingHistory();
type HistView = 'results' | 'bids' | 'feed';
let histView: HistView = 'results';
/** The lot the Bids view shows; null follows the lot in the ring. */
let bidsLotId: string | null = null;
let onlineOnly = false;
/** ADR-70 cut 6g: the sale checks panel (sale-checks.ts), read once a minute
 *  while the page is in view. */
const checks = new SaleChecks();
/** The ring's clock less this one's, from the last snapshot, so time in the
 *  ring counts from the ring's own timestamps. */
let clockSkewMs = 0;
/** ADR-70 cut 6e: the max bids the ring showed this screen, while their sheet
 *  is open; dropped when it closes, so no figure outlives the look. */
let shownMax: MaxBidsFrame | null = null;

/**
 * What the clerk DID, in the words they would use.
 *
 * The engine's event log stays authoritative and untouched; this is the
 * operational history that belongs on a working screen (brief section 23).
 * "SEQ 412 FLOOR_BID_ACCEPTED" is a debugging artifact, and making an operator
 * read it all sale is asking them to translate our data model in their head.
 */
interface RingResult {
  lotId: string;
  hipNumber: string;
  /** ADR-65: VOIDED is the act of reversing a sale; a SOLD row whose sale was
   *  reversed stays on the list with `voided` set, so the history reads
   *  "sold, then voided" rather than pretending the sale never happened. */
  outcome: 'SOLD' | 'NO SALE' | 'PASSED' | 'VOIDED';
  price: number | null;
  paddle: string | null;
  /** A floor sale nobody claimed. The office has to chase it, so it is said. */
  unattributed: boolean;
  /** The LOT_SOLD seq, on a SOLD row: what a VOID_SALE has to name. */
  soldSeq: number | null;
  voided: boolean;
  /** ADR-70 cut 6c: a PASSED / NO SALE row whose lot was reopened. */
  reopened?: boolean;
}

/** ADR-65: the current lot's sale as the FLOOR snapshot carries it (server
 *  snapshotOf, floor channel only). Absent on rings older than the field. */
interface CurrentSale {
  soldSeq: number;
  price: number;
  paddle: string | null;
  voidedSeq: number | null;
}
const results: RingResult[] = [];


const lotById = (id: string | null | undefined): Lot | undefined => lots.find((l) => l.id === id);
const ordered = (): Lot[] => [...lots].sort((a, b) => (a.runOrder ?? 1e9) - (b.runOrder ?? 1e9) || a.hipNumber.localeCompare(b.hipNumber, undefined, { numeric: true }));
const bidding = (): boolean => snap?.lotState === 'OPEN' || snap?.lotState === 'FAIR_WARNING';
/** ADR-60 cut 5: the lots selling on the one bid, from the snapshot. */
const sellingGroupOf = (): { lotIds: string[]; mode: 'ALL_TO_GO' | 'X_MONEY' | 'CHOICE'; multiplier: number } | null =>
  (snap?.['sellingGroup'] as { lotIds: string[]; mode: 'ALL_TO_GO' | 'X_MONEY' | 'CHOICE'; multiplier: number } | null | undefined) ?? null;
/** ADR-60 cut 2: the ring's one pending internet bid, from the snapshot. */
const pendingBidOf = (): { pendingBidId: string; amount: number; expiresAt: number | null } | null =>
  (snap?.['pendingBid'] as { pendingBidId: string; amount: number; expiresAt: number | null } | null | undefined) ?? null;
/** ADR-70 cut 6d: the step the ring will use on this lot - its own (the
 *  sale's table, or the one the clerk chose), never a table the console
 *  guesses. An older ring does not say, and the default table stands in. */
const incView = (): IncrementView => incrementView(snap ?? null, incrementAt(snap?.currentAsk ?? 0));
const keyCtx = (): KeyContext => ({ bidding: bidding(), currentAsk: snap?.currentAsk ?? null, currentHighBid: snap?.currentHighBid ?? null, increment: incView().step });
/** ADR-70 cut 6d: the lot this console last saw taken out of the ring. The
 *  ring then has no lot, so Next and Prev count from this one. */
let removedLotId: string | null = null;

/**
 * ADR-70: the screen may be acted on - connected, the ring has answered this
 * connection with a snapshot, and nothing is being caught up. While it is not,
 * the ring commands are switched off (disabled, not hidden: the clerk should
 * see that SOLD is there and why it cannot be pressed), and the client
 * refuses anything that still gets through (the keyboard).
 */
const ringLive = (): boolean => ring?.ringLive === true;
/** What render() last drew, so a health tick re-renders only on a change. */
let drawnLive = false;

// ---- ring commands ------------------------------------------------------
/**
 * Send a floor command and tell the clerk how it ended.
 *
 * The ring answers every command (ADR-70): accepted says nothing - the board
 * moving is the answer - and a refusal is one line in the toast and the feed,
 * never a dialog. A refusal for a stale screen arrives with the fresh
 * snapshot already redrawn, so the line only has to say "check and press
 * again". `auto` marks a command the console sent by itself.
 */
function floor(command: Record<string, unknown>, origin: 'press' | 'auto' = 'press'): void {
  if (!ring) return say('not connected to the ring', 'err');
  void ring.floor(command).then((r) => {
    const line = answerLine(r, origin);
    if (!line) return;
    if (line.toast) say(line.text, 'err');
    pushFeed(`✕ ${line.text}`);
  });
}
/** A command on the lot in the ring, sent with the view it was decided on. */
const guarded = (command: Record<string, unknown>, opts: { ask?: boolean } = {}, origin: 'press' | 'auto' = 'press'): void =>
  floor(withExpected(command, snap, opts), origin);
const cmd = {
  setLot: (lotId: string, origin: 'press' | 'auto' = 'press') => guarded({ type: 'SET_CURRENT_LOT', lotId }, {}, origin),
  /** ADR-65: reverse a sale this ring made. Names the exact LOT_SOLD, so a
   *  stale screen can never void a resale it has not seen. */
  voidSale: (lotId: string, soldSeq: number, kind: 'VOID' | 'TURN_BACK', reason: string) => floor({ type: 'VOID_SALE', lotId, soldSeq, kind, reason }),
  open: (lotId: string, openingAsk: number, origin: 'press' | 'auto' = 'press') => floor({ type: 'OPEN_LOT', lotId, openingAsk }, origin),
  /** ADR-60 cut 5: open the current lot together with the next lots in the run
   *  order, selling on one bid as a pen (all to go), by the unit (X money) or
   *  as the buyer's choice. The lead is the current lot. */
  openGroup: (lotIds: string[], mode: 'ALL_TO_GO' | 'X_MONEY' | 'CHOICE', openingAsk: number, multiplier?: number) =>
    floor({ type: 'OPEN_LOT', lotId: lotIds[0], openingAsk, group: { lotIds, mode, ...(multiplier !== undefined ? { multiplier } : {}) } }),
  /** A floor bid at an amount other than the ask is a CUT bid: the clerk moves
   *  the ask there and takes it. ONE command with `cutAsk` - the reducer emits
   *  ASK_SET + BID_ACCEPTED in a single atomic transition (ADR-42 D1), so an
   *  online bid can never slip into the cut ask between "move" and "take" the
   *  way it could when this was two frames. */
  /**
   * The paddle is OPTIONAL, and usually absent.
   *
   * A clerk taking bids off the floor is recording an amount going up while
   * the auctioneer chants - they cannot know who is bidding, and stopping to
   * ask on every bid would stall the ring on the first one. The number is
   * collected once, from the winner, after the hammer (see askWhoWon). When
   * the clerk DOES know the bidder - the paper-clerk strip, where a ringman
   * calls a number out - it rides along and is authoritative.
   */
  floorBid: (atAsk: number, paddleNumber?: string) => {
    if (!snap?.lotId) return;
    const cutAsk = snap.currentAsk !== null && atAsk !== snap.currentAsk;
    floor({ type: 'PLACE_FLOOR_BID', lotId: snap.lotId, atAsk, idempotencyKey: `${snap.lotId}:${atAsk}:${paddleNumber ?? 'floor'}:${Date.now()}`, ...(paddleNumber ? { paddleNumber } : {}), ...(cutAsk ? { cutAsk: true } : {}) });
  },
  // The ask buttons compute the new ask FROM the one on screen, so that ask is
  // part of the view: a second clerk's move in between refuses this one.
  setAsk: (ask: number) => snap?.lotId && guarded({ type: 'SET_ASK', lotId: snap.lotId, ask, reason: 'clerk' }, { ask: true }),
  // ADR-70 cut 3: one button walks the close - going once, going twice, last
  // call - on a ring that takes SET_CLOSE_STEP; the fair warning on one that
  // does not.
  fairWarning: () => { const a = closeStepAction(snap); if (a) guarded(a.command); },
  hammer: () => snap?.lotId && guarded({ type: 'HAMMER', lotId: snap.lotId }),
  sold: (winningPaddleNumber?: string) => {
    const l = lotById(snap?.lotId);
    if (!snap?.lotId) return;
    // ADR-60 cut 5: a CHOICE group needs the winner's pick before it can sell.
    const group = sellingGroupOf();
    let selectedLotIds: string[] | undefined;
    if (group?.mode === 'CHOICE') {
      const hips = group.lotIds.map((id) => lotById(id)?.hipNumber ?? id).join(', ');
      const answer = prompt(`Buyer's choice of ${group.lotIds.length} (${saleWords().lowPlural} ${hips}). Which ${saleWords().lowPlural} did the buyer take? Comma-separated:`);
      if (answer === null) return;
      const wanted = answer.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean);
      selectedLotIds = group.lotIds.filter((id) => wanted.includes(lotById(id)?.hipNumber ?? id) || wanted.includes(id));
      if (selectedLotIds.length === 0) { say(`No ${saleWords().lowPlural} matched - the buyer must take at least one`, 'err'); return; }
    }
    guarded({ type: 'CONFIRM_SOLD', lotId: snap.lotId, pricingMode: l?.pricingMode ?? 'PER_LOT', ...(winningPaddleNumber ? { winningPaddleNumber } : {}), ...(selectedLotIds ? { selectedLotIds } : {}) });
  },
  noSale: () => snap?.lotId && guarded({ type: 'DECLARE_NO_SALE', lotId: snap.lotId }),
  passed: () => snap?.lotId && guarded({ type: 'DECLARE_PASSED', lotId: snap.lotId }),
  /** The hammered lot, or (ADR-70 cut 6c) the ring's last pass or no sale,
   *  which may not be the lot in the ring. */
  reopen: (lotId: string, reason: string) => guarded({ type: 'REOPEN_LOT', lotId, reason }),
  /** A ring older than ADR-70 cut 6c: the floor-bid correction only. */
  undoFloor: (reason: string) => lastFloorBidSeq !== null && floor({ type: 'CORRECT_FLOOR_BID', reversesSeq: lastFloorBidSeq, reason }),
  /** ADR-70 cut 6c: take back the last bid, whatever its source. Names it, so
   *  a bid landing in between refuses this rather than removing that one. */
  retract: (reversesSeq: number, reason: string) => snap?.lotId && guarded({ type: 'RETRACT_LAST_BID', lotId: snap.lotId, reversesSeq, reason }),
  /** ADR-70 cut 6c: clear the board and start again at `ask`. */
  reset: (ask: number, clearMaxBids: boolean, reason: string) => snap?.lotId && guarded({ type: 'RESET_BIDDING', lotId: snap.lotId, ask, clearMaxBids, reason }),
  /** ADR-70 cut 6d: the lot's step; null = back to the sale's table. The ask
   *  previews on the sheet are read from the ask, so it is part of the view. */
  setIncrement: (step: number | null) => snap?.lotId && guarded({ type: 'SET_INCREMENT', lotId: snap.lotId, step }, { ask: true }),
  /** ADR-70 cut 6d: the lot leaves the ring unsold and stays in the sale. */
  removeFromRing: (reason: string) => snap?.lotId && guarded({ type: 'REMOVE_FROM_RING', lotId: snap.lotId, ...(reason ? { reason } : {}) }),
  /** ADR-70 cut 6e: look at the maxes on the lot in the ring. The ring
   *  records the look and answers this screen alone (onMaxBids). */
  viewMaxBids: () => snap?.lotId && floor({ type: 'VIEW_MAX_BIDS', lotId: snap.lotId }),
  /** ADR-70 cut 2: the auctioneer had the floor first at the amount the
   *  standing online bid holds. Names that bid, so a bid landing in between
   *  refuses this rather than handing the floor a bid nobody looked at. */
  awardEqual: (bidSeq: number, reason: string) => snap?.lotId && guarded({ type: 'AWARD_EQUAL_BID_ONSITE', lotId: snap.lotId, bidSeq, reason }),
  /** Undo that award: the online bid stands again, on the record. */
  restoreEqual: (overrideSeq: number, reason: string) => snap?.lotId && guarded({ type: 'RESTORE_EQUAL_BID_ONLINE', lotId: snap.lotId, overrideSeq, reason }),
  pause: () => floor({ type: 'PAUSE_ONLINE' }),
  resume: () => floor({ type: 'RESUME_ONLINE' }),
  // ADR-60 cut 2: operator-accepted internet bids.
  acceptOnline: (pendingBidId: string) => floor({ type: 'ACCEPT_ONLINE_BID', pendingBidId }),
  declineOnline: (pendingBidId: string) => floor({ type: 'DECLINE_ONLINE_BID', pendingBidId, reason: 'declined by the operator' }),
  setConfirmMode: (enabled: boolean, reason?: string) => floor({ type: 'SET_CONFIRM_MODE', enabled, ...(reason ? { reason } : {}) }),
  /** End the sale: finalize the ring for settlement. Irreversible - the reducer
   *  then rejects every command, so SALE_FINALIZED is the authoritative last
   *  event. Clerk-only and refused mid-lot (both enforced by the reducer). */
  endSale: () => floor({ type: 'FINALIZE_SALE' }),
};

// Set once SALE_FINALIZED is observed - the ring is sealed; the whole console
// goes read-only and points the operator at the office's Close & Settle.
let saleFinalized = false;
/** Show the finalized UI. Driven by BOTH the live SALE_FINALIZED event and the
 *  snapshot's finalizedSeq, so a page reload after End Sale restores it. */
function markFinalized(finalSeq: number): void {
  saleFinalized = true;
  $('finalized-seq').textContent = String(finalSeq);
  $('finalized-banner').style.display = '';
}

// ---- rendering ----------------------------------------------------------
function say(text: string, cls: 'ok' | 'err' | '' = ''): void {
  const el = $('toast'); el.textContent = text; el.className = 'toast ' + cls; el.style.opacity = '1';
  setTimeout(() => { el.style.opacity = '0'; }, 2500);
}
/** Record a finished lot and repaint the history panel. */
function pushResult(r: RingResult): void {
  results.unshift(r);
  if (results.length > 40) results.pop();
  renderResults();
}

/** A lot that ended without a sale. No price, no buyer, nothing to chase. */
function pushDisposition(lotId: string, outcome: 'NO SALE' | 'PASSED'): void {
  pushResult({ lotId, hipNumber: lotById(lotId)?.hipNumber ?? '-', outcome, price: null, paddle: null, unattributed: false, soldSeq: null, voided: false });
}

/**
 * ADR-65: the reversal, two taps and a typed reason, in place. The first tap
 * turns the button into the choice that matters (a clerical VOID or a
 * contractual TURN BACK); the second, on either, asks why and sends. It
 * disarms itself after a few seconds so a stray tap never leaves a live
 * reversal button armed. The ring refuses a stale soldSeq (NO_SUCH_SALE), a
 * second void (SALE_ALREADY_VOIDED) and an empty reason (REASON_REQUIRED).
 */
const VOID_ARM_MS = 4_000;
function askAndVoid(lotId: string, hip: string, soldSeq: number, kind: 'VOID' | 'TURN_BACK'): void {
  // Not worth typing a reason the ring cannot take yet (ADR-70).
  if (!ringLive()) return say(DEGRADED_LINE, 'err');
  const what = kind === 'VOID' ? 'void the sale' : 'turn back the sale';
  const reason = prompt(`Reason to ${what} of ${saleWords().low} ${hip} (this goes in the sale record)`);
  if (reason === null) return;
  if (reason.trim() === '') return say('a reason is required to reverse a sale', 'err');
  cmd.voidSale(lotId, soldSeq, kind, reason.trim());
}

/** A result row's "Void..." control: one button that becomes the two choices. */
function voidControl(r: RingResult): HTMLElement {
  const holder = el('span', { class: 'rchoice' });
  let timer: number | null = null;
  const disarm = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    setChildren(holder, arm);
  };
  const arm: HTMLButtonElement = el('button', { class: 'rvoid', text: 'Void…' });
  arm.onclick = () => {
    const pick = (kind: 'VOID' | 'TURN_BACK', label: string): HTMLButtonElement => {
      const b: HTMLButtonElement = el('button', { class: 'rvoid go', text: label });
      b.onclick = () => { disarm(); askAndVoid(r.lotId, r.hipNumber, r.soldSeq!, kind); };
      return b;
    };
    const cancel: HTMLButtonElement = el('button', { class: 'rvoid', text: '×' });
    cancel.onclick = disarm;
    setChildren(holder, pick('VOID', 'Void'), pick('TURN_BACK', 'Turn back'), cancel);
    timer = window.setTimeout(disarm, VOID_ARM_MS);
  };
  setChildren(holder, arm);
  return holder;
}

function renderResults(): void {
  const box = $('results');
  // Empty means one quiet line, not a tablet-sized white box held open all
  // sale for something that has not happened yet.
  $('histpanel').classList.toggle('empty', histView === 'results' && results.length === 0);
  if (results.length === 0) {
    setChildren(box, el('div', { class: 'rr-empty', text: 'None yet' }));
    return;
  }
  setChildren(box, ...results.map((r) => {
    const what =
      r.outcome === 'SOLD'
        ? el('span', { class: 'rwhat' },
            el('b', { text: `SOLD ${usd(r.price)}` }),
            r.paddle ? ` · paddle ${r.paddle}` : '',
            r.unattributed ? el('span', { class: 'rwarn', text: ' · NO BUYER NUMBER' }) : '',
            r.voided ? el('span', { class: 'rtag', text: ' · voided' }) : '',
          )
        : r.outcome === 'VOIDED'
          ? el('span', { class: 'rwhat' }, el('b', { text: `VOIDED ${usd(r.price)}` }), r.paddle ? ` · was paddle ${r.paddle}` : '')
          : el('span', { class: 'rwhat' }, el('b', { text: r.outcome }), r.reopened ? el('span', { class: 'rtag', text: ' · reopened' }) : '');
    const cls = r.outcome === 'SOLD' ? (r.voided ? 'sold voided' : 'sold') : r.outcome === 'VOIDED' ? 'void' : '';
    // ADR-65: a standing sale can be reversed from its row, so a void found
    // three hips later does not mean going back and disturbing the ring.
    const action = r.outcome === 'SOLD' && !r.voided && r.soldSeq !== null && !saleFinalized ? voidControl(r) : '';
    // ADR-70 cut 6a: the hip opens that lot's bids, for the question that
    // comes after a sale ("who was under him?").
    const hip: HTMLButtonElement = el('button', { class: 'rhip', text: r.hipNumber, attrs: { 'aria-label': `Bids on ${saleWords().low} ${r.hipNumber}` } });
    hip.onclick = () => showBidsFor(r.lotId);
    return el('div', { class: `rr ${cls}` },
      hip,
      what,
      action,
    );
  }));
}

// ---- ADR-70 cut 6a: bid history and pace (hitlist sections 15, 16) --------
const HIST_TITLES: Record<HistView, string> = { results: 'Recent results', bids: 'Bid history', feed: 'Ring activity' };

function setHistView(v: HistView): void {
  histView = v;
  $('results').hidden = v !== 'results';
  $('bids').hidden = v !== 'bids';
  $('feed').hidden = v !== 'feed';
  $('hist-title').textContent = HIST_TITLES[v];
  document.querySelectorAll<HTMLButtonElement>('#hist-views button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset['view'] === v)));
  // The empty-results line belongs to the Results view only.
  $('histpanel').classList.toggle('empty', v === 'results' && results.length === 0);
  if (v === 'bids') renderBids();
}

/** The Bids view on one lot; the lot in the ring when it is that one. */
function showBidsFor(lotId: string): void {
  bidsLotId = lotId === snap?.lotId ? null : lotId;
  setHistView('bids');
  maybeReadHistory();
}

/** This ring's run for the pace: its lots, less scratched lots and notices. */
const paceLotIds = (): string[] => lots.filter((l) => l.kind !== 'notice' && l.status !== 'scratched').map((l) => l.id);
const ringClock = (ms: number): string => new Date(ms - clockSkewMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function renderPace(): void {
  const panel = document.getElementById('pacepanel');
  if (!panel) return;
  // A timed sale closes on the clock, not at a ring: no pace to keep.
  panel.hidden = snap?.['mode'] === 'TIMED';
  if (panel.hidden) return;
  const c = paceCells(history.activity, { now: Date.now() + clockSkewMs, lotIds: paceLotIds(), clock: ringClock });
  $('pace-inring').textContent = c.inRing;
  $('pace-avg').textContent = c.avgPerLot;
  $('pace-lph').textContent = c.lotsPerHour;
  $('pace-last10').textContent = c.last10;
  $('pace-done').textContent = c.complete;
  $('pace-left').textContent = c.left;
  $('pace-end').textContent = c.estEnd;
  const note = historyNote(history.status);
  $('pace-note').textContent = note ?? '';
  $('pace-note').hidden = note === null;
}

function renderBids(): void {
  if (histView !== 'bids') return;
  const lotId = bidsLotId ?? snap?.lotId ?? null;
  const lot = lotById(lotId);
  const act = lotId ? history.activity.lots[lotId] : undefined;
  $('bids-lot').textContent = lotId ? `${saleWords().cap} ${lot?.hipNumber ?? lotId}${act ? ` · ${act.bidCount} bid${act.bidCount === 1 ? '' : 's'}` : ''}` : 'No lot in the ring';
  ($('bids-back') as HTMLButtonElement).hidden = bidsLotId === null;
  const rows = bidRows(history.activity, lotId, { onlineOnly });
  const note = historyNote(history.status);
  $('bids-note').textContent = rows === null ? 'Reading the bids on this lot…' : note ?? '';
  $('bids-note').hidden = rows !== null && note === null;
  if (rows === null) { setChildren($('bidlist')); return; }
  if (rows.length === 0) {
    setChildren($('bidlist'), el('div', { class: 'rr-empty', text: onlineOnly ? 'No online bids on this lot' : 'No bids on this lot yet' }));
    return;
  }
  // Every string from a bidder or a reason is a TEXT node (el text / append).
  setChildren($('bidlist'), ...rows.map((b) => {
    const marks = bidMarks(b);
    const time = new Date(b.at - clockSkewMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
    const via = bidViaWords(b);
    return el('div', { class: 'br' + (b.undone ? ' undone' : '') },
      el('span', { class: 'ba', text: usd(b.amount) }),
      el('span', { class: 'bw', text: bidderWords(b, regs) }),
      el('span', { class: 'bs' + (b.source === 'FLOOR' ? '' : ' on'), text: bidSourceWord(b.source) }),
      el('span', { class: 'bt', text: via ? `${time} · ${via}` : time }),
      ...marks.map((m) => el('span', { class: 'bm' + (m.kind === 'UNDONE' ? ' undo' : ''), text: m.text })),
    );
  }));
}

/** Read the ring's record now; `lotIds` asks for those lots' bids as well. */
async function readRingActivity(lotIds: readonly string[] = []): Promise<void> {
  if (!sale || history.busy) return;
  history.begin(Date.now(), lotIds);
  const q = new URLSearchParams();
  // One ring: the API's first ring is this one. Several: say which.
  if (multiRing && myRing) q.set('ringId', myRing.engineRingId);
  if (lotIds.length) q.set('lotIds', lotIds.join(','));
  const qs = q.toString();
  let reply: ActivityReply | null = null;
  try {
    const r = await fetch(`${API}/t/${encodeURIComponent(session.tenantSlug)}/sales/${encodeURIComponent(sale.id)}/ring-activity${qs ? `?${qs}` : ''}`, {
      headers: identityHeaders(session.apiIdentity()),
      credentials: 'include',
    });
    if (r.ok) reply = parseActivityReply(await r.json());
  } catch {
    // Said by the history's status: the rows are the ones since this screen opened.
  }
  history.settle(reply);
  renderPace();
  renderBids();
  $('bidcount').textContent = String((snap?.lotId ? history.activity.lots[snap.lotId]?.bidCount : undefined) ?? 0);
}

/** Read when something was missed, or when the Bids view wants a lot the
 *  history does not carry. */
function maybeReadHistory(): void {
  const now = Date.now();
  if (history.shouldRead(now)) { void readRingActivity(bidsLotId ? [bidsLotId] : []); return; }
  if (histView === 'bids' && bidsLotId !== null && history.wantsBidsFor(bidsLotId, now)) void readRingActivity([bidsLotId]);
}

// ---- ADR-70 cut 6g: sale checks (hitlist section 19) ----------------------
async function readSaleChecks(): Promise<void> {
  if (!sale || checks.busy) return;
  checks.begin(Date.now());
  renderChecks();
  let report = null;
  try {
    report = parseSaleHealth(await api.call('saleHealth', { params: { saleId: sale.id }, identity: session.apiIdentity() }));
  } catch {
    // Said by the panel: unavailable, or the older report marked as older.
  }
  checks.settle(Date.now(), report);
  renderChecks();
}

function maybeReadChecks(): void {
  if (checks.shouldRead(Date.now(), document.visibilityState === 'visible')) void readSaleChecks();
}

/** Every string is a TEXT node: a finding names lots and sellers. */
const checkLine = (l: ChecksLine): HTMLElement => el('li', { class: `ck-line ${l.tone}` },
  el('span', { class: 'k', text: l.label }),
  el('span', { class: 't', text: l.text }),
  ...(l.hips !== null ? [el('span', { class: 'h', text: l.hips })] : []),
);

function renderChecks(): void {
  const v = checksView(checks, Date.now(), saleWords().cap);
  const head = $('checks-head');
  head.textContent = v.head;
  head.className = `ck-head ${v.tone}`;
  setChildren($('checks-list'), ...v.lines.map(checkLine));
  $('checks-list').hidden = v.lines.length === 0;
  $('checks-more').textContent = v.more ?? '';
  $('checks-more').hidden = v.more === null;
  const adv = $('checks-adv') as HTMLDetailsElement;
  adv.hidden = v.advisories.length === 0;
  $('checks-adv-sum').textContent = `${v.advisories.length} ${v.advisories.length === 1 ? 'advisory' : 'advisories'}`;
  setChildren($('checks-adv-list'), ...v.advisories.map(checkLine));
  $('checks-note').textContent = v.note ?? '';
  $('checks-note').hidden = v.note === null;
}

function pushFeed(line: string): void {
  feed.unshift(line);
  if (feed.length > 60) feed.pop();
  // Each line is a TEXT node - feed content includes untrusted data (hip
  // numbers, titles, bidder names), so it must never be parsed as HTML.
  setChildren($('feed'), ...feed.map((f) => el('div', { class: 'fl', text: f })));
}

/** Session identity → request headers for routes NOT on the typed client
 *  (buyer-card mint is deliberately off the api-contract). Mirrors the client. */
function identityHeaders(id: Identity): Record<string, string> {
  const h: Record<string, string> = { 'x-requested-with': 'hipnumber' };
  if (id.bearer) h['authorization'] = 'Bearer ' + id.bearer;
  if (id.devStaff) h['x-dev-staff'] = `${id.devStaff.tenantId}:${id.devStaff.role}`;
  if (id.tenantHint) h['x-tenant-id'] = id.tenantHint;
  return h;
}

/**
 * Print a bidder card: mint the Smart Buyer Card (or, on a reprint, rotate it
 * after confirming), render the printable card IN THIS PAGE (paddle + a QR whose
 * URL carries the token only in a #fragment), and print. The raw token never
 * enters a URL the server can log, and staff never see a "credential" - they hit
 * Print Bidder Card and 184 + QR comes out (iPad/AirPrint-friendly).
 */
async function printBuyerCard(regId: string, paddleHint?: number | null): Promise<void> {
  const tbase = `${API}/t/${encodeURIComponent(session.tenantSlug)}`;
  const post = (url: string) => fetch(url, { method: 'POST', headers: { ...identityHeaders(session.apiIdentity()), 'content-type': 'application/json' }, credentials: 'include', body: '{}' });
  try {
    let r = await post(`${tbase}/registrations/${encodeURIComponent(regId)}/buyer-card`);
    if (r.status === 409) {
      // A card already exists for this bidder. Reprinting reissues it and
      // invalidates the current QR - confirm before replacing.
      if (!confirm('A bidder card already exists for this buyer. Reprint? This invalidates the current card.')) return;
      const st = await fetch(`${tbase}/registrations/${encodeURIComponent(regId)}/buyer-card`, { headers: identityHeaders(session.apiIdentity()), credentials: 'include' });
      const sj = (await st.json()) as { exists?: boolean; id?: string };
      if (!sj.exists || !sj.id) { say('could not locate the existing card', 'err'); return; }
      r = await post(`${tbase}/buyer-cards/${sj.id}/rotate`);
    }
    if (!r.ok) { say(`buyer card failed (${r.status})`, 'err'); return; }
    const j = (await r.json()) as { token: string; paddle?: number | null };
    const paddle = (j.paddle ?? paddleHint) ?? '';
    // The QR encodes the token in a #fragment - never sent to the server. The
    // buyer app has its OWN origin in production (b.hipnum.com), decoupled from
    // whatever origin the console runs on - so the printed QR is deterministic
    // and needs no operator flag. Dev serves the buyer page from this origin.
    const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    const buyerBase = isLocal ? `${location.origin}/b.html` : 'https://b.hipnum.com/';
    const buyerUrl = `${buyerBase}#${encodeURIComponent(j.token)}`;
    $('pc-paddle').textContent = `#${paddle}`;
    $('pc-sale').textContent = sale?.title ?? '';
    await QRCode.toCanvas($('pc-qr') as HTMLCanvasElement, buyerUrl, { width: 190, margin: 1, errorCorrectionLevel: 'M' });
    window.print();
    say('bidder card sent to print', 'ok');
  } catch (e) {
    say(`buyer card failed: ${String(e)}`, 'err');
  }
}

/**
 * One line when everything works, the detail one click away.
 *
 * Four always-visible diagnostics reading "connected / 1 ms / OPEN / 6" is
 * four things to check that never change and one thing to notice when it
 * does. When the system is fine the system should shut up; the moment it is
 * not, the chip says which part in words - never colour alone.
 */
function renderSystemChip(): void {
  const menu = $('sysmenu');
  const ringBad = !/connected/i.test($('h-ring').textContent ?? '');
  const paused = snap?.onlineStatus === 'PAUSED';
  const slow = $('h-lat').className.includes('bad');

  const state = paused ? 'paused' : ringBad || slow ? 'warn' : 'ok';
  menu.className = 'menu sysmenu' + (state === 'ok' ? '' : ' ' + state);
  $('sys-label').textContent =
    paused ? 'ONLINE PAUSED'
      : ringBad ? 'Ring offline'
        : slow ? 'Ring slow'
          : 'System healthy';
  $('sys-summary').setAttribute('aria-label', `System: ${$('sys-label').textContent}. Open for diagnostics.`);
}

/**
 * Sold, so bring up the next horse.
 *
 * Driven by the ring's LOT_SOLD EVENT rather than by the click on SOLD. The
 * lot is not sold because somebody pressed a button; it is sold when the ring
 * says so, and keying off the event means a second console watching the same
 * ring follows along instead of sitting on a finished lot.
 *
 * The pause is deliberate. Advancing the instant the hammer lands snatches the
 * confirmation away before the clerk has seen which paddle bought it, and that
 * is the one moment they most need to read.
 */
/** States a lot can be left in by a result the ring has finished with. */
const SETTLED: readonly string[] = ['SOLD', 'NO_SALE', 'PASSED'];

/**
 * Move to the next horse once a lot has a result.
 *
 * Every disposition advances, not just a sale: a passed horse is as finished
 * as a sold one, and leaving the ring parked on it makes the clerk do the
 * work the run order already knows how to do.
 *
 * The next horse comes from nextInRunOrder, which skips scratches and
 * anything already disposed. Advancing by position in the catalog would open
 * a scratched horse for bidding in front of a full house.
 */
function advanceAfterResult(finishedLotId: string): void {
  const next = nextInRunOrder(ordered(), finishedLotId);
  if (!next) return;   // end of the run, or a lot that is not in it: stay put
  window.setTimeout(() => {
    // Only if nothing has moved on in the meantime - another clerk may have
    // already set a lot, or this one may have gone back to correct something.
    // Never from a screen that cannot be trusted (ADR-70): after a reconnect
    // the clerk advances by hand from what the ring says.
    if (ringLive() && snap?.lotId === finishedLotId && SETTLED.includes(snap.lotState)) cmd.setLot(next.id, 'auto');
  }, 1600);
}

/**
 * ADR-70 cut 6d: a lot taken out of the ring is not coming now, so the run
 * moves on exactly as it does after a pass. The removed lot is still in the
 * sale; Prev (or the lot list) brings it back.
 */
function advanceAfterRemoval(removed: string): void {
  const next = nextInRunOrder(ordered(), removed);
  if (!next) return;
  window.setTimeout(() => {
    if (ringLive() && snap?.lotId === null && removedLotId === removed) cmd.setLot(next.id, 'auto');
  }, 1600);
}

/**
 * "Number, please?"
 *
 * Called when the clerk confirms a sale. Returns true if it took over, false
 * if the sale should just go through.
 *
 * It only interrupts when the buyer is genuinely unknown: the winning bid came
 * off the FLOOR and carried no paddle. An online winner is already identified
 * by their account, and a floor bid the clerk attributed at the time keeps
 * that attribution, so neither is worth a dialog. That inference is the whole
 * point - the console works out that an anonymous high bid must belong to
 * somebody in the room, and asks once, about one horse, at the only moment
 * anyone in the ring actually knows the answer.
 *
 * Nothing is sent until Save. Escape backs out to HAMMERED_PENDING, which is
 * a legal place to stand: reopen and no-sale are both still available there.
 */
function askWhoWon(): boolean {
  if (!highIsFloor || highPaddle !== null) return false;
  const lot = lotById(snap?.lotId);
  $('sold-hip').textContent = lot?.hipNumber ?? '-';
  $('sold-price').textContent = usd(snap?.currentHighBid);
  const input = $<HTMLInputElement>('sold-paddle');
  input.value = '';
  $('soldsheet').hidden = false;
  input.focus();
  return true;
}

function closeWhoWon(): void {
  $('soldsheet').hidden = true;
}

/**
 * ADR-70 cut 6c: "Reset bidding". The strongest correction on the screen -
 * every bid on the lot comes off the board - so it gets its own sheet: what
 * goes, where bidding starts again (the lot's opening ask unless changed),
 * why, and whether max bids go too. Nothing is sent until its red button.
 */
function openReset(): void {
  const lot = lotById(snap?.lotId);
  $('reset-hip').textContent = lot?.hipNumber ?? '-';
  $('reset-high').textContent = usd(snap?.currentHighBid);
  $<HTMLInputElement>('reset-ask').value = lot ? String(openingAskFor(lot)) : String(snap?.currentAsk ?? '');
  $<HTMLInputElement>('reset-reason').value = '';
  $<HTMLInputElement>('reset-maxes').checked = false;
  $('reset-err').textContent = '';
  $('resetsheet').hidden = false;
  $<HTMLInputElement>('reset-reason').focus();
}

function closeReset(): void {
  $('resetsheet').hidden = true;
}

function sendReset(): void {
  const ask = Number($<HTMLInputElement>('reset-ask').value.replace(/[$,\s]/g, ''));
  const reason = $<HTMLInputElement>('reset-reason').value.trim();
  if (!Number.isFinite(ask) || ask <= 0) { $('reset-err').textContent = 'Enter the amount bidding starts again at'; return $<HTMLInputElement>('reset-ask').focus(); }
  if (!reason) { $('reset-err').textContent = 'Say why - it is kept on the record, never shown to bidders'; return $<HTMLInputElement>('reset-reason').focus(); }
  closeReset();
  cmd.reset(ask, $<HTMLInputElement>('reset-maxes').checked, reason);
}

/**
 * ADR-70 cut 6d: choose the lot's increment. Steps around the table's own,
 * the steps this clerk typed before (kept on this device), a box for another,
 * and back to the table. Each choice shows the ask it leaves, by the ring's
 * rule, so the auctioneer sees the number before it is called. One tap sends.
 */
const STEPS_KEY = 'hn.console.incrementSteps';
function savedSteps(): number[] {
  try { return parseSavedSteps(localStorage.getItem(STEPS_KEY)); } catch { return []; }
}
function saveSteps(steps: number[]): void {
  try { localStorage.setItem(STEPS_KEY, JSON.stringify(steps)); } catch { /* private window: steps last this visit only */ }
}
let incSheetKey = '';
function openIncrement(): void {
  incSheetKey = '';
  $<HTMLInputElement>('inc-custom').value = '';
  $('inc-err').textContent = '';
  $('incsheet').hidden = false;
  paintIncrement();
  (document.querySelector<HTMLButtonElement>('#inc-choices button:not([aria-pressed="true"])') ?? $<HTMLInputElement>('inc-custom')).focus();
}
function closeIncrement(): void {
  $('incsheet').hidden = true;
}
/** Rebuilt only when what it shows changed, so a press is never lost to a repaint. */
function paintIncrement(): void {
  const iv = incView();
  const high = snap?.currentHighBid ?? null;
  const ask = snap?.currentAsk ?? null;
  const saved = savedSteps();
  const key = JSON.stringify([snap?.lotId, iv, high, ask, saved]);
  if (key === incSheetKey) return;
  incSheetKey = key;
  $('inc-hip').textContent = lotById(snap?.lotId)?.hipNumber ?? '-';
  $('inc-cur').textContent = usd(iv.step);
  $('inc-tablenote').textContent = iv.chosen && iv.table !== null ? ` (the sale's table says ${usd(iv.table)})` : iv.chosen ? '' : " (the sale's table)";
  const option = (step: number): HTMLButtonElement => {
    const current = iv.chosen && step === iv.step;
    const after = askAfterStep(high, ask, step);
    const b = el('button', { class: 'incopt', attrs: { type: 'button', 'aria-pressed': String(current) } },
      el('span', { class: 'a', text: usd(step) }),
      el('span', { class: 'd', text: current ? 'in use now' : after !== null ? `ask ${usd(after)}` : '' }));
    if (!current) b.onclick = () => chooseStep(step);
    return b;
  };
  setChildren($('inc-choices'), ...(iv.table !== null ? incrementChoices(iv.table) : []).map(option));
  setChildren($('inc-saved'), ...saved.map((step) => {
    const current = iv.chosen && step === iv.step;
    const use = el('button', { text: usd(step), attrs: { type: 'button', 'aria-pressed': String(current), 'aria-label': current ? `${usd(step)}, in use now` : `Use ${usd(step)}` } });
    if (!current) use.onclick = () => chooseStep(step);
    const x = el('button', { class: 'x', text: '✕', attrs: { type: 'button', 'aria-label': `Forget ${usd(step)}` } });
    x.onclick = () => { saveSteps(forgetStep(savedSteps(), step)); paintIncrement(); };
    return el('span', { class: 'incchip' }, use, x);
  }));
  // Back to the table: only when a step was chosen; says the table's step
  // when the ring names it.
  const back = $<HTMLButtonElement>('inc-table');
  back.hidden = !iv.chosen;
  const tableAsk = iv.table !== null ? askAfterStep(high, ask, iv.table) : null;
  back.textContent = iv.table !== null ? `Back to the table (${usd(iv.table)}${tableAsk !== null && high !== null ? ` · ask ${usd(tableAsk)}` : ''})` : 'Back to the table';
  $('inc-cancel').textContent = `Cancel - keep ${usd(iv.step)}`;
}
function chooseStep(step: number | null): void {
  closeIncrement();
  cmd.setIncrement(step);
}
function useTypedStep(): void {
  const step = parseStep($<HTMLInputElement>('inc-custom').value);
  if (step === null) { $('inc-err').textContent = 'Enter a whole dollar amount, $1 or more'; return $<HTMLInputElement>('inc-custom').focus(); }
  const iv = incView();
  if (iv.chosen && step === iv.step) { $('inc-err').textContent = 'That is already the increment'; return $<HTMLInputElement>('inc-custom').focus(); }
  saveSteps(rememberStep(savedSteps(), step));
  chooseStep(step);
}

/**
 * ADR-70 cut 6e (hitlist 9.2): the max bids the ring showed this screen.
 * Read-only: a look, not a control. It is the ring's answer at one moment,
 * so it closes when the lot in the ring changes or a Super Admin turns
 * visibility off, and Show again takes a fresh (and again recorded) look.
 */
/**
 * ADR-70 cut 6f (hitlist 10 and 11): the Online interest panel. Shown only
 * while the floor snapshot carries the operator-intent controls (a Super
 * Admin turned them on); a ring older than 6f carries none, so no panel.
 * Hitlist 10.2's highlights: subtle for a pointer on Bid, stronger for a
 * confirmation open, one flash for a bid just sent.
 */
function paintInterest(): void {
  const on = !!snap && snap['intentSignals'] != null;
  $('interestpanel').hidden = !on;
  if (!on) { interest = null; interestFlashed = new Set(); return; }
  if (!interest) {
    $('interest-sum').textContent = 'Waiting for the ring';
    $('interest-sum').hidden = false;
    setChildren($('interest-lines'));
    $('interest-present').hidden = true;
    return;
  }
  const v = interestView(interest, snap?.lotId ?? null, regs);
  $('interest-sum').textContent = v.summary;
  $('interest-sum').hidden = v.summary === '';
  const before = interestFlashed;
  interestFlashed = new Set(v.lines.map((l) => l.key));
  setChildren($('interest-lines'), ...(v.lines.length
    ? v.lines.map((l) => el('li', { class: l.tone + (l.tone === 'submitted' && !before.has(l.key) ? ' flash' : '') },
        el('span', { class: 'int-who', text: l.who }),
        el('span', { class: 'int-what', text: l.what })))
    : [el('li', { class: 'interest-empty', text: v.empty ?? '' })]));
  $('interest-present').hidden = !v.present || v.present.length === 0;
  if (v.present) {
    $('interest-present-n').textContent = String(v.present.length + v.presentMore);
    setChildren($('interest-present-list'),
      ...v.present.map((w) => el('li', { text: w })),
      v.presentMore > 0 && el('li', { class: 'more', text: `and ${v.presentMore} more` }));
  }
}

function openMaxBids(f: MaxBidsFrame): void {
  shownMax = f;
  const v = maxBidsView(f, regs);
  $('max-hip').textContent = lotById(f.lotId)?.hipNumber ?? '-';
  $('max-role').textContent = v.role;
  $('max-tab').hidden = v.rows.length === 0;
  setChildren($('max-rows'), ...v.rows.map((r) => el('tr', {},
    el('td', { text: r.who }),
    el('td', { class: r.amount === 'Hidden' ? 'r hid' : 'r', text: r.amount }),
    el('td', { class: r.left === 'Hidden' ? 'r hid' : 'r', text: r.left }))));
  const at = new Date(f.asOf - clockSkewMs).toLocaleTimeString();
  $('max-note').textContent = `${v.empty ? `${v.empty} ` : ''}As the ring stood at ${at}. This look is on the ring's record.`;
  $('maxsheet').hidden = false;
  $<HTMLButtonElement>('max-close').focus();
}
function closeMaxBids(): void {
  shownMax = null;
  setChildren($('max-rows'));
  $('maxsheet').hidden = true;
}

/**
 * Read the typed paddle back as a HUMAN, live, before the sale commits.
 *
 * "247 · John Smith" is the cross-check that catches a transposed digit while
 * it is still free to fix. A number that belongs to nobody, or to a bidder who
 * is not approved, says so HERE rather than becoming an unattributable
 * purchase row that the office untangles after the sale.
 *
 * It never blocks. The clerk was in the room and the auctioneer said a number;
 * the console's job is to show what it knows, not to overrule a person who can
 * see the bidder. Save stays live either way.
 */
function renderPaddleCheck(): void {
  const raw = $<HTMLInputElement>('sold-paddle').value.trim();
  const out = $('sold-who');
  if (!raw) return setChildren(out, '');
  const n = Number(raw);
  if (!Number.isInteger(n)) return setChildren(out, el('span', { class: 'no', text: 'not a paddle number' }));
  const matches = findByPaddle(regs, n);
  const m = matches[0];
  if (!m) return setChildren(out, el('span', { class: 'no', text: `no bidder holds paddle ${n}` }));
  const tail = phoneTail(m.reg.bidderPhone);
  const warn = saleWarnings({
    price: snap?.currentHighBid ?? 0,
    currentHighBid: snap?.currentHighBid ?? null,
    paddle: raw,
    match: { approvalStatus: m.reg.approvalStatus, bidLimitCents: m.reg.bidLimitCents },
  });
  setChildren(
    out,
    el('b', { text: m.reg.bidderName ?? `account ${m.reg.accountId.slice(0, 8)}` }),
    tail ? ` · ***${tail}` : '',
    ...warn.map((w) => el('span', { class: 'no', text: ` · ${w.message}` })),
  );
}

/**
 * A lot arriving in the ring IS the lot opening.
 *
 * There is no separate moment in a real sale where somebody declares bidding
 * open - the horse walks in and the auctioneer starts asking. Making the clerk
 * tap "Open lot" for every hip was our state machine leaking into their hands.
 *
 * Fired from the CURRENT_LOT_SET EVENT rather than from the snapshot, and that
 * distinction is the safety: it opens when the ring MOVES to a lot during this
 * session, so reloading the console never opens a horse that was sitting
 * closed. The button stays as the fallback if the ring refuses.
 */
function autoOpenOnArrival(lotId: string): void {
  if (saleFinalized) return;
  // ADR-70 cut 3 (Q5): a ring set to start lots paused stages the horse and
  // waits for the operator - Open is pressed, never sent for them.
  if (snap?.['startLotsPaused'] === true) return;
  const lot = lotById(lotId);
  if (!lot) return;   // not in the catalog: a human decides
  window.setTimeout(() => {
    if (!ringLive() || snap?.lotId !== lotId || snap.lotState !== 'UPCOMING') return;
    // ADR-65: the clerk went BACK to a sold hip. Its sale stands; the ring
    // would refuse the open (LOT_ALREADY_SOLD), so do not even ask - the
    // void button on the action row is the way forward.
    const cs = (snap['currentSale'] as CurrentSale | null | undefined) ?? null;
    if (cs !== null && cs.voidedSeq === null) return;
    cmd.open(lotId, openingAskFor(lot), 'auto');
  }, 400);
}

function render(): void {
  const lot = lotById(snap?.lotId);
  const state = snap?.lotState ?? '-';
  $('hipno').textContent = lot?.hipNumber ?? (snap?.lotId ? snap.lotId : '-');
  const a = lot?.attributes ?? {};
  $('lotdt').textContent = lot ? [a['foalYear'], a['color'], a['sex'], a['heightHands'] ? `${a['heightHands']} hh` : null, lot.kind !== 'equine' ? lot.kind : null].filter(Boolean).join(' · ') || lot.kind : '';
  // ADR-70 cut 3: a closing lot says the step called; a staged lot on a ring
  // that starts lots paused says it is waiting for the operator.
  const waiting = snap?.lotState === 'UPCOMING' && snap['startLotsPaused'] === true && !!snap.lotId;
  $('lotstate').textContent = closeStepWords(snap)?.toUpperCase() ?? (waiting ? 'WAITING - PRESS OPEN' : state);
  $('lotstate').className = 'state ' + state.toLowerCase();
  // The NAME and the descriptor are one string in the catalog ("Buck - 7yo
  // Quarter Horse gelding"). Split on the separator so the name can carry the
  // weight it deserves; a title with no separator is all name, which is the
  // safe way to be wrong.
  const parts = splitTitle(lot?.title ?? null);
  $('lotname').textContent = parts.name ?? (snap?.lotId ? '(not in catalog)' : 'No lot loaded');
  $('lotbreed').textContent = parts.descriptor ?? '';
  // Who consigned it - the name, because that is what gets said out loud.
  // Absent for a lot added at the ring, and absence renders as nothing rather
  // than as "Consigned by -".
  $('consignor').textContent = lot?.consignorName ? `Consigned by ${lot.consignorName}` : '';
  // ADR-60 cut 5: what the bid buys when several lots are in the ring.
  const group = sellingGroupOf();
  const groupEl = document.getElementById('groupline');
  if (groupEl) {
    groupEl.hidden = !group;
    if (group) {
      const hips = group.lotIds.map((id) => lotById(id)?.hipNumber ?? id).join(', ');
      const plural = saleWords().lowPlural;
      groupEl.textContent = group.mode === 'ALL_TO_GO' ? `ALL TO GO · ${plural} ${hips} · one price for the pen`
        : group.mode === 'X_MONEY' ? `X MONEY · ${plural} ${hips} · bid × ${group.multiplier}${snap?.currentHighBid != null ? ` = ${usd(snap.currentHighBid * group.multiplier)}` : ''}`
        : `CHOICE · ${plural} ${hips} · buyer takes one or more at the bid each`;
    }
  }
  const groupBtn = document.getElementById('btn-group') as HTMLButtonElement | null;
  if (groupBtn) groupBtn.disabled = !(snap?.lotState === 'UPCOMING' && !!lot && nextInRunOrder(ordered(), lot.id) !== null);
  // The horse. A photo when the catalog has one; the panel simply collapses
  // when it does not, rather than holding a grey rectangle open all sale.
  const heroSrc = imageUrl(lot, 260);
  const hero = $('lothero');
  hero.style.backgroundImage = heroSrc ? `url("${heroSrc.replace(/["\\]/g, '')}")` : '';
  hero.style.display = heroSrc ? '' : 'none';
  // With a photo the state badge sits on the photo's corner and costs nothing.
  // Without one it falls back to the panel's corner, where a long registered
  // name would otherwise run underneath it, so the name block yields the space.
  ($('lothead') as HTMLElement).classList.toggle('nophoto', !heroSrc);
  const vids = videoCount(lot);
  $('lotvideo').textContent = vids ? `${vids} video${vids === 1 ? '' : 's'} in the catalog` : '';
  $('lotvideo').style.display = vids ? '' : 'none';
  // Pricing mode (§11.5): what the bid MEANS. Per-head shows the projected
  // lot total (bid × quantity); choice shows the group size.
  const pv = pricingView(lot?.pricingMode ?? 'PER_LOT', snap?.currentHighBid ?? null, lot?.quantity ?? 1);
  $('curbid').textContent = usd(snap?.currentHighBid);
  // Who is holding the bid. "floor · number at hammer" is the honest label for
  // an anonymous raised hand: the clerk should be able to see at a glance that
  // this buyer is not yet identified, rather than find out at settlement.
  $('cursrc').textContent =
    snap?.currentHighBid == null ? '-'
    : highPaddle ? `paddle ${highPaddle} (floor)`
    : highIsFloor || snap.highBidderRef === 'Floor' ? 'floor · number at hammer'
    : 'online bidder';
  $('pricing').textContent = pv.mode === 'PER_LOT' ? '' : pv.totalDiffers ? `${pv.label} → total ${usd(pv.total)}` : pv.label;
  $('pricing').style.display = pv.mode === 'PER_LOT' ? 'none' : '';
  $('asking').textContent = usd(snap?.currentAsk);
  const iv = incView();
  $('incv').textContent = usd(iv.step);
  $('incset').hidden = !iv.chosen;
  $('btn-inc').title = iv.settable ? 'Choose the increment for this lot' : '';
  $('bidcount').textContent = String((snap?.lotId ? history.activity.lots[snap.lotId]?.bidCount : undefined) ?? 0);
  $('reserve').textContent = lot?.reserveCents != null ? (snap?.currentHighBid != null && snap.currentHighBid * 100 >= lot.reserveCents ? 'reserve MET' : `reserve ${usd(lot.reserveCents / 100)} (clerk only)`) : 'no reserve';
  $('reserve').className = 'fine ' + (lot?.reserveCents != null && snap?.currentHighBid != null && snap.currentHighBid * 100 >= lot.reserveCents ? 'ok' : '');
  // increments
  const ask = snap?.currentAsk ?? 0;
  const inc = iv.step;
  $('i-down').textContent = usd(Math.max(inc, ask - inc)); $('i-cur').textContent = usd(ask); $('i-500').textContent = usd(ask + inc); $('i-1000').textContent = usd(ask + inc * 2);
  // buttons by state
  const st = snap?.lotState;
  const enable = (id: string, on: boolean) => { ($(id) as HTMLButtonElement).disabled = !on; };
  enable('btn-open', st === 'UPCOMING' && !!lot);
  // Scratching is a catalog fact, not a ring one. Offered while no money is on
  // the horse: UPCOMING, or OPEN with an empty board. The OPEN case is not a
  // loophole, it is the normal one now that a lot opens itself the moment it
  // arrives - "upcoming" lasts about 400ms, and a horse that turns out not to
  // be running usually turns out that way with the ask already showing.
  // Never once a bid exists: a horse taking bids did not fail to show up.
  enable('btn-scratch', !!lot && (st === 'UPCOMING' || (st === 'OPEN' && snap?.currentHighBid == null)));
  // Leave the label alone while it is armed - render() runs on every snapshot
  // and would otherwise wipe "Confirm scratch of 107?" mid-decision.
  if (!$('btn-scratch').classList.contains('armed')) $('btn-scratch').textContent = 'Scratch';
  $('btn-open').textContent = lot ? `Open at ${usd(openingAskFor(lot))}` : 'Open lot';
  enable('btn-floor', bidding());
  // Say the price it takes. A one-tap button that commits money should not be
  // ambiguous about which number it commits.
  $('btn-floor').textContent = snap?.currentAsk != null && bidding() ? `Bid ${usd(snap.currentAsk)}` : 'Record floor bid';
  // Never leave the dialog hanging over a lot that has moved on - another
  // clerk selling it, a reopen, or a reconnect all land here.
  if (!$('soldsheet').hidden && st !== 'HAMMERED_PENDING') closeWhoWon();
  // Nor the reset sheet over a lot that has stopped taking bids.
  if (!$('resetsheet').hidden && !bidding()) closeReset();
  const closeNext = closeStepAction(snap);
  enable('btn-once', closeNext !== null);
  $('btn-once').textContent = closeNext?.label ?? 'Going once';
  enable('btn-hammer', bidding() && snap?.currentHighBid != null);
  enable('btn-sold', st === 'HAMMERED_PENDING');
  enable('btn-nosale', st === 'HAMMERED_PENDING');
  enable('btn-pass', bidding());
  // Reopen mirrors the reducer: the hammered lot (ADR-42 D2), or (ADR-70 cut
  // 6c) the ring's last pass or no sale while the lot in the ring holds no
  // bid - the ring works that out and puts it on the floor snapshot. SOLD is
  // a settlement fact: it is voided or turned back, never reopened.
  const reopenable = reopenableOf(snap?.['reopenable']);
  enable('btn-reopen', reopenAllowed(st, reopenable));
  const reopenTo = reopenTarget(snap ?? null, reopenable);
  $('btn-reopen').textContent = reopenTo && reopenTo.from !== 'HAMMERED'
    ? `Reopen ${saleWords().low} ${lotById(reopenTo.lotId)?.hipNumber ?? '?'} (${reopenTo.from === 'PASSED' ? 'passed' : 'no sale'})`
    : 'Reopen lot';
  // ADR-70 cut 6c: take back the last bid whatever its source, named on the
  // button so the clerk sees which one goes. A ring older than that has only
  // the floor-bid correction, which stays for it.
  const fix = bidCorrectionOffer(snap ?? null);
  enable('btn-undo', fix.legacy ? lastFloorBidSeq !== null && bidding() : fix.retract !== null);
  $('btn-undo').textContent = fix.retract ? `Remove last bid (${usd(fix.retract.amount)} ${SOURCE_WORDS[fix.retract.source]})` : fix.legacy ? 'Undo last floor bid' : 'Remove last bid';
  enable('btn-reset', !fix.legacy && fix.reset);
  // ADR-70 cut 2: equal bids. Offered only when the ring would take it (its
  // policy, a lot taking bids, the bid the button names still standing).
  const equal = equalBidOffer(snap);
  enable('btn-award', equal.award !== null);
  enable('btn-restore', equal.restore !== null);
  if (equal.award) $('btn-award').textContent = `Floor had ${usd(equal.award.amount)} first`;
  if (equal.restore) $('btn-restore').textContent = `Undo - online had ${usd(equal.restore.amount)}`;
  // ADR-65: the clerk went back to a sold hip (or the board still reads SOLD).
  // The sale stands until it is reversed on the record: Open is refused by the
  // ring (LOT_ALREADY_SOLD), so it is not offered; the reversal is, saying
  // exactly what stands. A void from the result rows covers a lot that is
  // not up; this covers the one that is.
  const cs = (snap?.['currentSale'] as CurrentSale | null | undefined) ?? null;
  const standing = !!lot && cs !== null && cs.voidedSeq === null && (st === 'UPCOMING' || st === 'SOLD');
  if (standing) enable('btn-open', false);
  enable('btn-void', standing && !saleFinalized);
  enable('btn-turnback', standing && !saleFinalized);
  // ADR-70 cut 6d: the increment while the lot takes bids, on a ring that
  // takes SET_INCREMENT; out of the ring while it holds no bid and has no
  // result (never a lot whose sale stands - that is a void or a turn back).
  enable('btn-inc', iv.settable && bidding() && !saleFinalized);
  enable('btn-remove', removeFromRingOffer(snap ?? null) && !standing && !saleFinalized);
  if (!$('incsheet').hidden) { if (!bidding()) closeIncrement(); else paintIncrement(); }
  // ADR-70 cut 6e (hitlist 9.2): that maxes stand, always; Show only when a
  // Super Admin turned visibility on. An open look closes when the lot moves
  // on or visibility goes off: what it showed is no longer what stands.
  const ml = maxBidLine(snap);
  $('maxline').textContent = ml?.text ?? ' ';
  $('btn-maxshow').hidden = !ml?.canView;
  enable('btn-maxshow', !!ml?.canView && !saleFinalized);
  if (shownMax && (snap?.lotId !== shownMax.lotId || snap?.['maxBidVisibility'] !== true)) closeMaxBids();
  paintInterest();
  if (standing && !$('btn-void').classList.contains('armed')) $('btn-void').textContent = `Void sale (SOLD ${usd(cs.price)}${cs.paddle ? ` · paddle ${cs.paddle}` : ''})`;
  if (standing && !$('btn-turnback').classList.contains('armed')) $('btn-turnback').textContent = 'Turn back';
  // End Sale: allowed only with no live/provisional lot (mirrors the reducer),
  // and never once finalized.
  const lotActive = st === 'OPEN' || st === 'FAIR_WARNING' || st === 'HAMMERED_PENDING';
  enable('btn-endsale', !!snap && !saleFinalized && !lotActive);
  // ADR-60 cut 2: the pending internet bid, and the confirm-mode switch (which
  // the reducer refuses mid-lot, so it is offered only between lots).
  const pending = pendingBidOf();
  ($('pendingpanel') as HTMLElement).hidden = !pending || !bidding();
  if (pending) {
    $('pending-amt').textContent = usd(pending.amount);
    $('pending-sub').textContent = pending.expiresAt ? `waiting for you · expires ${new Date(pending.expiresAt).toLocaleTimeString()}` : 'waiting for you';
  }
  const confirmOn = snap?.['confirmMode'] === true;
  $('btn-confirm').textContent = confirmOn ? 'Confirm mode: ON - internet bids wait for you' : 'Confirm mode: off - internet bids auto-accept';
  $('btn-confirm').className = 'pausebtn confirmbtn ' + (confirmOn ? 'on' : '');
  enable('btn-confirm', !!snap && !saleFinalized && !lotActive);
  document.querySelectorAll<HTMLButtonElement>('.inc').forEach((b) => { b.disabled = !bidding(); });

  // ---- the state machine drives what is even VISIBLE -----------------------
  // A screen of pale grey buttons reads as "half this software is broken",
  // when what it actually means is "not yet". The enable rules above already
  // encode exactly when each action applies, so visibility follows them
  // rather than duplicating the logic: if it cannot be pressed, it is not
  // shown. Nothing here changes what is PERMITTED - a hidden control is still
  // disabled, and the reducer would refuse it anyway.
  for (const id of ['btn-open', 'btn-scratch', 'btn-floor', 'btn-once', 'btn-hammer', 'btn-sold', 'btn-nosale', 'btn-pass', 'btn-undo', 'btn-award', 'btn-restore', 'btn-reset', 'btn-reopen', 'btn-void', 'btn-turnback', 'btn-remove']) {
    const b = document.getElementById(id) as HTMLButtonElement | null;
    if (b) b.hidden = b.disabled;
  }
  // The ask controls and the floor-bid panel belong to a lot that is taking
  // bids. Between lots they are clutter.
  ($('incs') as HTMLElement).hidden = !bidding();
  ($('floorpanel') as HTMLElement).hidden = !bidding();
  // A row with nothing in it should not leave its gap behind.
  ($('actions') as HTMLElement).hidden = !document.querySelector('#actions button:not([hidden])');

  // The live figures get the room only while the figures are live.
  ($('bidrow') as HTMLElement).className = 'bidrow' + (bidding() ? ' bidding' : '');
  $('btn-pause').textContent = snap?.onlineStatus === 'PAUSED' ? `▶ Resume online (${snap.pauseReason ?? ''})` : '⏸ PAUSE NET';
  $('btn-pause').className = 'pausebtn ' + (snap?.onlineStatus === 'PAUSED' ? 'paused' : '');
  // next / prev
  const run = ordered(); const i = run.findIndex((l) => l.id === snap?.lotId);
  const next = run[i + 1]; const prev = i > 0 ? run[i - 1] : undefined;
  $('next-n').textContent = next ? `${saleWords().cap} ${next.hipNumber} · ${next.title}` : 'end of run order';
  $('prev-n').textContent = prev ? `${saleWords().cap} ${prev.hipNumber}` : '-';
  // catalog rail
  setChildren($('catlist'), ...run.map((l) =>
    el('button', { class: 'cat-item' + (l.id === snap?.lotId ? ' active' : '') + (l.status === 'sold' ? ' done' : ''), data: { id: l.id } },
      thumb(l),
      el('span', { class: 't', text: l.title }),
      el('span', { class: 's', text: l.status }),
    ),
  ));
  // Put the CURRENT lot at the top of the rail, so what she sees is this
  // horse and the next several rather than the whole catalog at once. The
  // sold ones are still a scroll away when she needs them - a run order is
  // not a spreadsheet, but it is also not a single row.
  const activeEl = $('catlist').querySelector<HTMLElement>('.cat-item.active');
  if (activeEl && activeEl !== lastScrolledTo) {
    lastScrolledTo = activeEl;
    $('catlist').scrollTop = activeEl.offsetTop - $('catlist').offsetTop;
  }
  // bidders rail
  const approved = regs.filter((r) => r.approvalStatus === 'APPROVED').slice(0, 40);
  setChildren($('bidderlist'), ...(approved.length
    ? approved.map((r) =>
        el('div', { class: 'bidder' + (snap?.currentHighBid != null && holdsHighBid(r, { highBidderId: typeof snap['highBidderId'] === 'string' ? snap['highBidderId'] : null, highPaddle, highIsFloor }) ? ' hot' : '') },
          el('span', { class: 'pad', text: String(r.livePaddle ?? r.onlinePaddle ?? '-') }),
          // The HUMAN, never the account id. "9651f5e0" is a database key that
          // means nothing to a clerk and cannot be read back to a bidder; it
          // was never information, only leakage. Falls back to a plain
          // "no name on file" so the row still reads as a sentence.
          el('span', { class: 'acc', text: r.bidderName ?? 'no name on file' }),
          r.bidLimitCents ? el('span', { class: 'lim', text: `≤ ${usd(r.bidLimitCents / 100)}` }) : false,
        ),
      )
    : [el('div', { class: 'fine', text: 'no approved bidders yet' })]));
  $('bidders-n').textContent = String(regs.filter((r) => r.approvalStatus === 'APPROVED').length);
  // keyboard strip
  const d = keyDisplay(keys, keyCtx());
  // These are real inputs now, so NEVER write over one the clerk is typing in.
  // The physical-keyboard reducer still drives them when it is the thing in
  // use; a finger on a tablet drives them directly. The hint text is the same
  // either way.
  const kAmount = $<HTMLInputElement>('k-amount');
  const kPaddle = $<HTMLInputElement>('k-paddle');
  if (document.activeElement !== kAmount) {
    kAmount.value = keys.amount ? String(Number(keys.amount)) : '';
    // Just the money. "at ask $500" read as a headline competing with the
    // Asking card; the figure alone reads as the default this field will use.
    kAmount.placeholder = snap?.currentAsk != null ? usd(snap.currentAsk) : 'amount';
  }
  if (document.activeElement !== kPaddle) {
    kPaddle.value = keys.paddle || '';
    kPaddle.placeholder = 'paddle';
  }
  $('k-hint').textContent = d.hint;
  kAmount.className = 'kv ' + (keys.field === 'amount' ? 'active' : '');
  kPaddle.className = 'kv ' + (keys.field === 'paddle' ? 'active' : '');
  // health
  $('h-online').textContent = snap ? (snap.onlineStatus === 'OPEN' ? 'OPEN' : `PAUSED · ${snap.pauseReason ?? ''}`) : '-';
  $('h-online').className = 'v ' + (snap?.onlineStatus === 'OPEN' ? 'ok' : 'warn');
  $('h-seq').textContent = String(snap?.seq ?? '-');
  renderSystemChip();
  renderPace();
  renderBids();

  // ADR-70: a screen the ring has not confirmed is shown, never acted on. The
  // controls the state machine offers stay where they are, switched off, and
  // one line says why; they come back the moment the ring answers. Pausing
  // the internet is not on the list - it is the one control that must work
  // while everything else is in doubt (the client sends it mid-resync).
  const live = ringLive();
  drawnLive = live;
  // Before the first snapshot there is no screen to distrust, only "Connecting".
  const degraded = !!ring && snap !== null && !saleFinalized && !live && !notAssigned;
  const banner = document.getElementById('degraded-banner');
  if (banner) {
    banner.hidden = !degraded;
    if (degraded) $('degraded-text').textContent = DEGRADED_LINE;
  }
  // The rules above set these on every render, so they only need switching off.
  if (degraded) {
    for (const id of ['btn-open', 'btn-group', 'btn-floor', 'btn-once', 'btn-hammer', 'btn-sold', 'btn-nosale', 'btn-pass', 'btn-undo', 'btn-award', 'btn-restore', 'btn-reset', 'btn-reopen', 'btn-void', 'btn-turnback', 'btn-confirm', 'btn-endsale', 'btn-inc', 'btn-remove', 'btn-maxshow']) {
      const e = document.getElementById(id) as HTMLButtonElement | null;
      if (e) e.disabled = true;
    }
    document.querySelectorAll<HTMLButtonElement>('.inc').forEach((b) => { b.disabled = true; });
  }
  // Nothing else sets these, so they are switched back on here too (the
  // finalized fence below still wins). The sold sheet stays open with the
  // typed paddle while its Save waits for the ring.
  for (const id of ['btn-next', 'btn-prev', 'btn-accept', 'btn-decline', 'btn-announce', 'sold-save', 'sold-skip', 'reset-go', 'inc-use', 'inc-table']) {
    const e = document.getElementById(id) as HTMLButtonElement | null;
    if (e) e.disabled = degraded;
  }

  // A finalized ring is a sealed record - force the whole console read-only so
  // nothing can be attempted after the fence (the reducer would reject it, but
  // the UI should not invite it).
  if (saleFinalized) {
    for (const id of ['btn-open', 'btn-scratch', 'btn-floor', 'btn-once', 'btn-hammer', 'btn-sold', 'btn-nosale', 'btn-pass', 'btn-undo', 'btn-award', 'btn-restore', 'btn-reset', 'btn-reopen', 'btn-void', 'btn-turnback', 'btn-next', 'btn-prev', 'btn-pause', 'btn-confirm', 'btn-accept', 'btn-decline', 'btn-endsale', 'btn-walkup', 'btn-addlot', 'paddle-go', 'btn-inc', 'btn-remove', 'btn-maxshow']) {
      const e = document.getElementById(id) as HTMLButtonElement | null;
      if (e) e.disabled = true;
    }
    document.querySelectorAll<HTMLButtonElement>('.inc').forEach((b) => { b.disabled = true; });
  }
}

// ---- events from the ring ----------------------------------------------
function onEvent(ev: Record<string, unknown>, seq: number): void {
  // ADR-70 cut 6a: the history first, so the count and the rows are right by
  // the time the snapshot that follows this event redraws them.
  history.fold({ ...ev, seq });
  const t = ev['type'] as string;
  switch (t) {
    case 'BID_ACCEPTED': {
      const fromFloor = ev['bidSource'] === 'FLOOR';
      if (fromFloor) lastFloorBidSeq = seq;
      highIsFloor = fromFloor;
      highPaddle = fromFloor ? ((ev['paddleNumber'] as string | null) ?? null) : null;
      pushFeed(`#${seq} ${fromFloor ? '🔨' : '⌁'} bid ${usd(ev['bidAmount'] as number)}${ev['paddleNumber'] ? ` · paddle ${ev['paddleNumber']}` : fromFloor ? '' : ` · ${ev['bidderRef']}`} → ask ${usd(ev['nextAsk'] as number)}`);
      break;
    }
    case 'LOT_OPENED': {
      lastFloorBidSeq = null; highPaddle = null; highIsFloor = false;
      const g = ev['group'] as { lotIds: string[]; mode: string; multiplier: number } | undefined;
      pushFeed(`#${seq} lot opened at ${usd(ev['openingAsk'] as number)}${g ? ` · ${g.mode.replace(/_/g, ' ').toLowerCase()} with ${g.lotIds.length} lots` : ''}`);
      break;
    }
    case 'LOT_GROUP_REMAINDER': pushFeed(`#${seq} choice: ${(ev['soldLotIds'] as string[]).map((id) => lotById(id)?.hipNumber ?? id).join(', ')} taken; ${(ev['lotIds'] as string[]).map((id) => lotById(id)?.hipNumber ?? id).join(', ')} back to the ring`); say('Choice remainder is back in the ring - open it next', 'ok'); break;
    case 'CURRENT_LOT_SET': {
      lastFloorBidSeq = null; highPaddle = null; highIsFloor = false;
      const arrived = ev['lotId'] as string;
      pushFeed(`#${seq} current lot → ${lotById(arrived)?.hipNumber ?? arrived}`);
      autoOpenOnArrival(arrived);
      break;
    }
    case 'HAMMER': pushFeed(`#${seq} HAMMER ${usd(ev['finalBid'] as number)} · ${highPaddle ? `paddle ${highPaddle}` : ev['winningBidderRef']}`); break;
    case 'LOT_SOLD': {
      const soldLot = lotById(ev['lotId'] as string);
      const pv = pricingView(ev['pricingMode'] as string, ev['price'] as number, soldLot?.quantity ?? 1);
      const money = pv.totalDiffers ? `${usd(ev['price'] as number)} ${pv.label} = ${usd(pv.total)}` : `${usd(ev['price'] as number)}${pv.mode !== 'PER_LOT' ? ` (${pv.label})` : ''}`;
      // The paddle ON the event, not the local one: after "number, please?" the
      // engine holds the attribution and this is the log line the clerk will be
      // read back in a dispute. NO BUYER NUMBER is stated plainly rather than
      // dressed up as an id, because it is the line the office has to chase.
      const wonBy = (ev['winningPaddleNumber'] as string | null) ?? null;
      const who = wonBy ? `paddle ${wonBy}` : ev['bidSource'] === 'FLOOR' ? 'NO BUYER NUMBER' : `buyer ${String(ev['buyerId'] ?? '').slice(0, 8)}`;
      pushFeed(`#${seq} SOLD ${money} · ${who}`);
      pushResult({
        lotId: ev['lotId'] as string,
        hipNumber: soldLot?.hipNumber ?? '-',
        outcome: 'SOLD',
        price: ev['price'] as number,
        paddle: wonBy,
        unattributed: !wonBy && ev['bidSource'] === 'FLOOR',
        soldSeq: seq,
        voided: false,
      });
      void refreshCatalog();
      advanceAfterResult(ev['lotId'] as string);
      break;
    }
    case 'SALE_VOIDED': {
      // ADR-65: the sale no longer stands. The SOLD row it reverses is kept and
      // marked (history is never erased on this screen either); the act gets
      // its own row. The reason is on the floor wire and in the log; it is
      // said here once, to the clerk who typed it or their relief.
      const voidedLot = lotById(ev['lotId'] as string);
      const soldSeq = ev['soldSeq'] as number;
      for (const r of results) if (r.outcome === 'SOLD' && r.soldSeq === soldSeq) r.voided = true;
      const paddle = (ev['winningPaddleNumber'] as string | null) ?? null;
      pushFeed(`#${seq} SALE VOIDED - ${saleWords().low} ${voidedLot?.hipNumber ?? ev['lotId']} sold at #${soldSeq} ${usd(ev['price'] as number)}${paddle ? ` · paddle ${paddle}` : ''} (${String(ev['kind']).toLowerCase().replace(/_/g, ' ')}: ${String(ev['reason'])})`);
      pushResult({ lotId: ev['lotId'] as string, hipNumber: voidedLot?.hipNumber ?? '-', outcome: 'VOIDED', price: ev['price'] as number, paddle, unattributed: false, soldSeq, voided: false });
      say(`Sale of ${saleWords().low} ${voidedLot?.hipNumber ?? ''} voided - re-offer it or move on`, 'ok');
      void refreshCatalog();
      break;
    }
    case 'LOT_NO_SALE': pushFeed(`#${seq} NO SALE`); pushDisposition(ev['lotId'] as string, 'NO SALE'); advanceAfterResult(ev['lotId'] as string); break;
    case 'LOT_PASSED': pushFeed(`#${seq} PASSED`); pushDisposition(ev['lotId'] as string, 'PASSED'); advanceAfterResult(ev['lotId'] as string); break;
    case 'FAIR_WARNING': pushFeed(`#${seq} fair warning`); break;
    // ADR-70 cut 3: each step of the close, and the way back to open.
    case 'CLOSE_STEP_SET': pushFeed(`#${seq} ${ev['step'] === null ? 'back to open' : (closeStepWords({ lotState: 'FAIR_WARNING', closeStep: ev['step'] }) ?? 'closing').toLowerCase()}`); break;
    case 'ASK_SET': pushFeed(`#${seq} ask → ${usd(ev['ask'] as number)}`); break;
    case 'FLOOR_BID_CORRECTED': lastFloorBidSeq = null; highPaddle = null; highIsFloor = false; pushFeed(`#${seq} floor bid reversed (${ev['reason']})`); break;
    case 'EQUAL_BID_OVERRIDDEN': {
      // ADR-70 cut 2. To the floor: its own BID_ACCEPTED follows and updates
      // the high bid. Back to online: no new bid - the online bid stands again.
      const toFloor = ev['awardedTo'] === 'ONSITE';
      if (!toFloor) { lastFloorBidSeq = null; highPaddle = null; highIsFloor = false; }
      const why = ev['cause'] === 'POLICY' ? 'ring policy' : String(ev['reason'] ?? '');
      pushFeed(`#${seq} ⚖ equal bid ${usd(ev['amount'] as number)} ${toFloor ? 'given to the floor' : 'back to online'}${why ? ` (${why})` : ''}`);
      break;
    }
    case 'ONLINE_PAUSED': pushFeed(`#${seq} ⏸ online paused (${ev['reason']})`); break;
    case 'ONLINE_RESUMED': pushFeed(`#${seq} ▶ online resumed`); break;
    // ADR-60 cut 2: operator-accepted internet bids and session leases.
    case 'ONLINE_BID_PENDING': pushFeed(`#${seq} ⌁ internet bid ${usd(ev['amount'] as number)} WAITING (${ev['bidderRef']}${ev['bidSource'] === 'PROXY' ? ', max bid' : ''})`); say('Internet bid waiting - accept or decline', 'ok'); break;
    case 'PENDING_BID_RESOLVED': pushFeed(`#${seq} ⌁ internet bid ${usd(ev['amount'] as number)} ${String(ev['result']).toLowerCase().replace(/_/g, ' ')}`); break;
    case 'CONFIRM_MODE_SET': pushFeed(`#${seq} confirm mode ${ev['enabled'] ? 'ON' : 'off'}${ev['reason'] ? ` (${ev['reason']})` : ''}`); break;
    case 'BIDDING_SESSION_TRANSFERRED': pushFeed(`#${seq} bidder ${String(ev['bidderId'] ?? '').slice(0, 8)} bidding from ${ev['previousSessionId'] ? 'another' : 'a'} device`); break;
    case 'LOT_REOPENED': {
      // ADR-70 cut 6c: a pass or no sale brought back. Its result row stays,
      // marked (history is never erased on this screen); the board is the
      // ring's own restore, read from the snapshot that follows.
      const from = ev['from'];
      const back = from === 'PASSED' || from === 'NO_SALE';
      const hip = lotById(ev['lotId'] as string)?.hipNumber ?? String(ev['lotId']);
      pushFeed(`#${seq} ${saleWords().low} ${hip} reopened${back ? ` after ${from === 'PASSED' ? 'pass' : 'no sale'}` : ''} (${ev['reason']})`);
      if (back) {
        const r = results.find((x) => x.lotId === ev['lotId'] && x.outcome === (from === 'PASSED' ? 'PASSED' : 'NO SALE') && !x.reopened);
        if (r) { r.reopened = true; renderResults(); }
      }
      break;
    }
    // ADR-70 cut 6c: a bid taken back (any source) and a board reset. The
    // reason is on the floor wire only; the public sees what happened.
    case 'BID_RETRACTED': {
      lastFloorBidSeq = null; highPaddle = null; highIsFloor = false;
      const src = ev['bidSource'];
      const word = src === 'FLOOR' || src === 'ONLINE' || src === 'PROXY' ? SOURCE_WORDS[src] : String(src ?? '').toLowerCase();
      const now = typeof ev['restoredHighBid'] === 'number' ? `bid back to ${usd(ev['restoredHighBid'] as number)}` : `no bid · ask ${usd(ev['restoredAsk'] as number)}`;
      pushFeed(`#${seq} ✕ removed bid #${ev['reversesSeq']} ${usd(ev['amount'] as number)} ${word} → ${now} (${ev['reason']})`);
      break;
    }
    case 'BIDDING_RESET': {
      lastFloorBidSeq = null; highPaddle = null; highIsFloor = false;
      const cleared = ev['clearMaxBids'] === true ? ` · ${ev['clearedMaxBids']} max bid${ev['clearedMaxBids'] === 1 ? '' : 's'} cleared` : '';
      pushFeed(`#${seq} ⟲ BIDDING RESET → starts at ${usd(ev['ask'] as number)}${cleared} (${ev['reason']})`);
      break;
    }
    // ADR-70 cut 6d: the lot's step changed (the ask with it when a bid was on
    // the board), and a lot taken out of the ring with no result.
    case 'INCREMENT_CHANGED': {
      const step = typeof ev['step'] === 'number' ? usd(ev['step'] as number) : "the sale's table";
      const moved = typeof ev['ask'] === 'number' && ev['ask'] !== ev['previousAsk'] ? ` · ask ${usd(ev['previousAsk'] as number)} → ${usd(ev['ask'] as number)}` : '';
      pushFeed(`#${seq} ↕ increment → ${step}${moved}`);
      break;
    }
    case 'LOT_REMOVED_FROM_RING': {
      lastFloorBidSeq = null; highPaddle = null; highIsFloor = false;
      const gone = ev['lotId'] as string;
      removedLotId = gone;
      const g = ev['group'] as { lotIds: string[] } | undefined;
      const hips = (g?.lotIds ?? [gone]).map((id) => lotById(id)?.hipNumber ?? id).join(', ');
      pushFeed(`#${seq} ⤺ ${saleWords().low} ${hips} taken out of the ring - unsold, still in the sale${ev['reason'] ? ` (${ev['reason']})` : ''}`);
      advanceAfterRemoval(gone);
      break;
    }
    // ADR-42: proxies (the clerk sees THAT a max exists / acted - never the amount;
    // redactForFloor guarantees maxAmount is absent on this wire), announcements,
    // pre-bid handoff, timed closes.
    case 'MAX_BID_PLACED': pushFeed(`#${seq} ⚙ max bid set by ${String(ev['bidderId'] ?? '').slice(0, 8)}${ev['replacesSeq'] ? ' (updated)' : ''}`); break;
    case 'MAX_BID_CANCELLED': pushFeed(`#${seq} ⚙ max bid cancelled by ${String(ev['bidderId'] ?? '').slice(0, 8)}`); break;
    case 'MAX_BID_INVALIDATED': pushFeed(`#${seq} ⚙ max bid invalidated (${ev['reason']})${ev['reconfirmRequested'] ? ' - reconfirm requested' : ''}`); break;
    case 'PROXY_EXHAUSTED': pushFeed(`#${seq} ⚙ proxy exhausted at ${usd(ev['atAsk'] as number)} (${ev['reason']})`); break;
    // ADR-70 cut 6e: a look at the maxes (the floor wire drops what it showed)
    // and the Super Admin's switch, so every console sees both happen.
    case 'MAX_BID_VIEWED': pushFeed(`#${seq} ⚙ ${maxBidViewedFeed(ev, lotById(ev['lotId'] as string)?.hipNumber ?? String(ev['lotId']), saleWords())}`); break;
    // Worded as the state, not the act: a ring also hears it at go-live.
    case 'RING_CAPABILITIES_CHANGED': pushFeed(`#${seq} ⚙ max-bid visibility is ${ev['operatorMaxBidVisibility'] === true ? 'on' : 'off'} for this company (a Super Admin's switch)`); break;
    case 'PREBID_HANDOFF': pushFeed(`#${seq} ⇥ pre-bids carried in: ${ev['activeMaxCount']} standing max(es) from ${usd(ev['openingAsk'] as number)}`); break;
    case 'ANNOUNCEMENT_POSTED': pushFeed(`#${seq} 📣 ${ev['severity'] === 'MATERIAL' ? 'MATERIAL ' : ''}announcement${ev['lotId'] ? ` (lot ${lotById(ev['lotId'] as string)?.hipNumber ?? ev['lotId']})` : ''}: ${String(ev['text'])}`); break;
    case 'TIMED_LOT_OPENED': pushFeed(`#${seq} ⏱ timed lot ${ev['lotId']} open, closes ${new Date(ev['closesAt'] as number).toLocaleTimeString()}`); break;
    case 'LOT_CLOSE_EXTENDED': pushFeed(`#${seq} ⏱ lot ${ev['lotId']} extended to ${new Date(ev['newCloseAt'] as number).toLocaleTimeString()} (#${ev['extensionNumber']}${ev['cascadedFromLotId'] ? `, via ${ev['cascadedFromLotId']}` : ''})`); break;
    case 'TIMED_LOT_CLOSED': pushFeed(`#${seq} ⏱ lot ${ev['lotId']} CLOSED ${ev['finalHighBid'] != null ? `at ${usd(ev['finalHighBid'] as number)}` : 'with no bids'}`); break;
    case 'TIMED_LOT_REOPENED': {
      // ADR-70 cut 6i: the office reopened a timed lot that closed with no
      // sale. Its result row stays, marked, as on LOT_REOPENED.
      const hip = lotById(ev['lotId'] as string)?.hipNumber ?? String(ev['lotId']);
      pushFeed(`#${seq} ⏱ ${saleWords().low} ${hip} reopened by the office after no sale, closes ${new Date(ev['closesAt'] as number).toLocaleTimeString()} (${ev['reason']})`);
      const r = results.find((x) => x.lotId === ev['lotId'] && x.outcome === 'NO SALE' && !x.reopened);
      if (r) { r.reopened = true; renderResults(); }
      break;
    }
    case 'SALE_FINALIZED': {
      const finalSeq = (ev['finalSeq'] as number | undefined) ?? seq;
      markFinalized(finalSeq);
      pushFeed(`#${seq} 🔒 SALE FINALIZED - ring sealed at ${finalSeq}`);
      say('Sale finalized - the office can now close & settle.', 'ok');
      render();
      break;
    }
    default: pushFeed(`#${seq} ${t}`);
  }
}

/**
 * ADR-70 cut 5f: this console's ring is not the clerk's. The ring has closed
 * the connection and will refuse it again, so the console steps aside for a
 * line saying whose ring this is and a link to the ring that is theirs. The
 * ring itself is untouched: the clerk the office assigned runs it.
 */
function showNotAssigned(rings: readonly string[]): void {
  notAssigned = true;
  const view = notAssignedView(rings, myRing ?? { engineRingId: params.get('ringId') ?? '', name: 'this ring' }, saleRings);
  const hrefFor = (ringId: string): string => { const q = new URLSearchParams(location.search); q.set('ringId', ringId); return `?${q.toString()}`; };
  setChildren($('gate'),
    el('p', { text: view.line }),
    ...view.go.map((r) => el('p', {}, el('a', { text: `Open ${r.name}`, attrs: { href: hrefFor(r.ringId) } }))),
    view.go.length > 0 && el('p', { text: 'If this is wrong, ask the office to change your ring.' }),
  );
  $('gate').style.display = ''; $('app').style.display = 'none';
  render();
}

// ---- data -----------------------------------------------------------------
async function refreshCatalog(): Promise<void> {
  if (!sale) return;
  saleLots = (await api.call('listLots', { params: { saleId: sale.id }, identity: session.apiIdentity() })) as unknown as Lot[];
  // ADR-70 cut 5c: with several rings, this console runs only its own ring's
  // lots. The server says which ring each lot is in (lot.ring).
  lots = multiRing && myRing ? saleLots.filter((l) => l.ring?.ringId === myRing!.engineRingId) : saleLots;
  regs = (await api.call('listRegistrations', { params: { saleId: sale.id }, identity: session.apiIdentity() })) as unknown as Registration[];
  render();
}

// ---- wiring ---------------------------------------------------------------
async function main(): Promise<void> {
  try {
    session = await bootSession({ apiBase: API, params, signInUrl: `${API.replace(':4100', ':4000')}/staff-signin.html` });
  } catch (e) {
    if (e instanceof NeedsSignIn) { setChildren($('gate'), el('p', { text: 'Signed out.' }), el('p', {}, el('a', { text: 'Staff sign-in', attrs: { href: e.signInUrl } }), ' (then come back here with ?t=<tenant>).')); return; }
    $('gate').textContent = (e as Error).message; return;
  }
  // The station chip (ADR-44): "Matt · Ring Clerk ▾", and clicking it moves
  // between stations. What used to sit here - role, tenant slug, DEV - is
  // developer readout, not sale-day information; it moved into the overflow
  // menu where somebody debugging can still find it.
  mountModeSwitcher({
    session,
    container: $('who'),
    current: modeByKey('ring') ?? { key: 'ring', label: 'Ring Clerk', desc: 'Run the live auction', page: 'index.html', roles: ['clerk', 'admin'] },
    // A lot mid-bid must not be walked away from by a station switch.
    canLeave: () => (bidding() ? 'Finish or pass the lot in the ring before switching stations.' : null),
    say: (m) => say(m, 'err'),
  });
  $('ctx').textContent = `${session.tenantSlug} · ${session.role}${session.mode === 'dev' ? ' · DEV' : ''}`;
  // The Displays board reuses the same session params (?dev= / ?t=).
  $('nav-displays').setAttribute('href', `displays.html${location.search}`);
  api = new ControlApiClient({ baseUrl: API, tenantSlug: session.tenantSlug });

  const sales = (await api.call('listSales', { identity: session.apiIdentity() })) as unknown as Sale[];
  const want = params.get('sale');
  sale = sales.find((s) => s.id === want || s.slug === want) ?? sales.find((s) => s.status === 'live') ?? sales.find((s) => s.status === 'published') ?? sales[0];
  if (!sale) { $('gate').textContent = 'No sales in this tenant.'; return; }
  // The sale's own word for a lot number, on every label in the page below.
  setSaleWords(sale);
  // ADR-70 cut 5c: a sale running several rings has one console per ring,
  // picked with ?ringId=ring-2. That is an engine ring id, never an address:
  // the address is still the server's provisioning for the ring picked.
  const prov = (sale.ringProvisioning ?? {}) as RingAddresses;
  saleRings = ringAddresses(prov);
  multiRing = saleRings.length > 1;
  myRing = pickRing(prov, params.get('ringId'));
  if (!myRing) { $('gate').textContent = `This sale has no ring ${params.get('ringId')}.`; return; }
  $('saletitle').textContent = multiRing ? `${sale.title} · ${myRing.name}` : sale.title;
  await refreshCatalog();
  $('gate').style.display = 'none'; $('app').style.display = '';

  // SECURITY: the ring endpoint is the server-provisioned one on the sale - a
  // clerk sends their Cognito token to it in HELLO, so a ?ring= override from
  // a (possibly phishing) URL must NOT redirect that token in production. The
  // override is honoured only in dev mode.
  const ringOverride = session.mode === 'dev' ? params.get('ring') : null;
  const ringUrl = ringOverride ?? myRing.endpoint ?? 'ws://localhost:4000';
  ring = new ProtocolClient({
    url: ringUrl, actorId: session.actorId, role: 'clerk',
    token: () => session.ringToken(),
    onSnapshot: (s) => {
      snap = s;
      // ADR-70: who holds the bid is read back from the ring every time, so a
      // reconnect or a stale refusal never leaves an older high bid on screen.
      ({ highIsFloor, highPaddle, lastFloorBidSeq } = highBidFromSnapshot(s, lastFloorBidSeq));
      if (s.finalizedSeq != null && !saleFinalized) markFinalized(s.finalizedSeq);
      // ADR-70 cut 6a: a snapshot ahead of the history means events were
      // missed (a reconnect, a gap); the ring's record fills them in.
      if (typeof s['serverNow'] === 'number') clockSkewMs = (s['serverNow'] as number) - Date.now();
      history.noteSnapshot(s.seq);
      maybeReadHistory();
      render();
    },
    onEvent: (ev, seq) => { onEvent(ev, seq); },
    // ADR-70 cut 6e: what Show asked for, to this screen alone.
    onMaxBids: (f) => {
      if (f.lotId !== snap?.lotId) return say('The lot moved on before the max bids came back - press Show again', 'err');
      openMaxBids(f);
    },
    // ADR-70 cut 6f: the Online interest panel, to clerk screens alone.
    onInterest: (f) => { interest = f; paintInterest(); },
    // A dropped connection drops the panel's last word too: the ring hands
    // a fresh one over with the next HELLO (cut 6f).
    onStatus: (s) => { $('h-ring').textContent = s; $('h-ring').className = 'v ' + (s === 'connected' ? 'ok' : 'warn'); if (s !== 'connected') interest = null; render(); },
    // ADR-70: the controls follow whether the screen may be acted on; health
    // also ticks with every pong, so redraw only when that answer changes.
    onHealth: (h) => { if (h.ringLive !== drawnLive) render(); },
    onLatency: (ms) => { $('h-lat').textContent = `${ms} ms`; $('h-lat').className = 'v ' + (ms < 150 ? 'ok' : ms < 400 ? 'warn' : 'bad'); },
    // A rejected command is SHOWN, never swallowed: the engine's reason goes
    // to the toast and the feed so the clerk can act.
    // ADR-70 cut 5f: the office gave this clerk another ring of the sale. The
    // ring names it in an ERROR, then closes for good; the close repeats it.
    onError: (m, detail) => {
      if (detail?.code === 'RING_NOT_ASSIGNED') { showNotAssigned(detail.rings ?? []); return; }
      if (m.startsWith('RING_NOT_ASSIGNED')) { if (!notAssigned) showNotAssigned([]); return; }
      say(m, 'err'); pushFeed(`✕ ${m}`);
    },
  });

  // buttons
  $('catlist').addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('.cat-item'); if (b?.dataset['id']) cmd.setLot(b.dataset['id']); });
  // One tap, no prompt. The button already SAYS the price it opens at, and the
  // ask is adjustable the moment the lot is live, so making the clerk type a
  // number through a modal bought nothing.
  $('btn-open').onclick = () => { const l = lotById(snap?.lotId); if (l) cmd.open(l.id, openingAskFor(l)); };
  // ADR-60 cut 5: open this lot with the next N in the run order as a group.
  const groupBtn = document.getElementById('btn-group');
  if (groupBtn) groupBtn.onclick = () => {
    const lead = lotById(snap?.lotId);
    if (!lead) return;
    const run = ordered();
    const i = run.findIndex((l) => l.id === lead.id);
    const countAnswer = prompt(`Sell ${saleWords().low} ${lead.hipNumber} together with how many of the NEXT lots in the run order?`, '1');
    if (countAnswer === null) return;
    const extra = Math.floor(Number(countAnswer));
    if (!Number.isFinite(extra) || extra < 1) return say('Enter how many lots to add', 'err');
    const lotIds = run.slice(i, i + 1 + extra).map((l) => l.id);
    if (lotIds.length < 2) return say('Not enough lots left in the run order', 'err');
    const modeAnswer = prompt(`${saleWords().capPlural} ${lotIds.map((id) => lotById(id)?.hipNumber ?? id).join(', ')}. Sell as:\n  A = all to go (one price for the pen)\n  X = X money (bid per unit, times the count)\n  C = buyer's choice (one or more at the bid each)`, 'A');
    if (modeAnswer === null) return;
    const m = modeAnswer.trim().toUpperCase();
    const mode = m.startsWith('X') ? 'X_MONEY' : m.startsWith('C') ? 'CHOICE' : m.startsWith('A') ? 'ALL_TO_GO' : null;
    if (!mode) return say('Answer A, X or C', 'err');
    cmd.openGroup(lotIds, mode, openingAskFor(lead));
  };
  /**
   * Record the floor bid the strip is holding.
   *
   * Empty amount means the standing ask, which is the common case and the
   * reason this is one tap: the clerk is keeping up with a chant, not filling
   * in a form. A typed amount is a CUT bid and the engine takes it atomically.
   * Empty paddle is normal too - nobody knows who is bidding until the hammer.
   */
  const recordFloorBid = (): void => {
    if (!bidding()) return;
    const kAmount = $<HTMLInputElement>('k-amount');
    const kPaddle = $<HTMLInputElement>('k-paddle');
    const typed = Number(kAmount.value.replace(/[^0-9.]/g, ''));
    const amount = Number.isFinite(typed) && typed > 0 ? Math.round(typed) : snap?.currentAsk ?? null;
    if (amount === null) return;
    const paddle = kPaddle.value.trim();
    cmd.floorBid(amount, paddle || undefined);
    kAmount.value = ''; kPaddle.value = '';
    keys = IDLE;
  };
  $('btn-floor').onclick = recordFloorBid;

  /**
   * Keep the orange commit button above the software keyboard.
   *
   * Centring the strip was not enough: measured on an iPad Air with a number
   * pad up, the fields cleared it and the BUTTON did not, by two pixels. A
   * clerk who can see what they typed but cannot reach the control that
   * records it is worse off than one who can see neither.
   *
   * visualViewport is what actually knows how tall the keyboard is -
   * window.innerHeight does not change when iOS raises one. Falls back to
   * innerHeight where the API is absent, which costs nothing on a desktop
   * because there is no keyboard to avoid there.
   */
  function keepCommitVisible(): void {
    const centre = document.querySelector<HTMLElement>('.center');
    if (!centre) return;
    const vv = window.visualViewport;
    const keyboard = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
    // Make the room FIRST. The column is sized to fit the screen exactly, so
    // on its own it has no scroll range at all and nothing can move out from
    // under the keyboard - the first version of this scrolled by the right
    // number of pixels into zero available space and changed nothing.
    centre.style.paddingBottom = keyboard > 0 ? `${keyboard}px` : '';
    if (keyboard === 0) return;
    const btn = $('btn-floor').getBoundingClientRect();
    const overflow = btn.bottom - (window.innerHeight - keyboard - 14);
    if (overflow > 0) centre.scrollBy({ top: overflow, behavior: 'smooth' });
  }
  /** Keyboard gone: give the column its shape back. */
  const releaseKeyboardRoom = (): void => {
    const centre = document.querySelector<HTMLElement>('.center');
    if (centre) centre.style.paddingBottom = '';
  };
  // The keyboard can appear, resize (autocorrect bar) or rotate after focus.
  window.visualViewport?.addEventListener('resize', () => {
    const active = document.activeElement;
    if (active === $('k-amount') || active === $('k-paddle')) keepCommitVisible();
  });

  // Enter commits from either field, so a tablet clerk never has to reach for
  // the button, and the software keyboard's own "done" key does the right
  // thing. Tab from amount lands on paddle by document order already.
  for (const id of ['k-amount', 'k-paddle']) {
    $(id).onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); recordFloorBid(); ($(id) as HTMLInputElement).blur(); } };
    // Give iOS time to actually raise the keyboard before measuring it.
    $(id).addEventListener('focus', () => { setTimeout(keepCommitVisible, 300); });
    $(id).addEventListener('blur', () => { setTimeout(releaseKeyboardRoom, 300); });
  }
  $('btn-once').onclick = () => cmd.fairWarning();
  $('btn-hammer').onclick = () => cmd.hammer();
  $('btn-sold').onclick = () => { if (!askWhoWon()) cmd.sold(); };
  const savePaddle = (): void => {
    const p = $<HTMLInputElement>('sold-paddle').value.trim();
    if (!p) return $<HTMLInputElement>('sold-paddle').focus();
    closeWhoWon();
    cmd.sold(p);
  };
  $('sold-save').onclick = savePaddle;
  // No number given. The horse still sells - a ring cannot be held up by a
  // bidder who wandered off - but it is said out loud in the feed rather than
  // discovered as a null buyer in settlement three hours later.
  $('sold-skip').onclick = () => { closeWhoWon(); say('sold with NO buyer number - the office must attribute this at checkout', 'err'); cmd.sold(); };
  $('sold-paddle').oninput = renderPaddleCheck;
  $('sold-paddle').onkeydown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); savePaddle(); }
    // Back out to HAMMERED_PENDING without selling. Reopen / no sale live there.
    if (e.key === 'Escape') { e.preventDefault(); closeWhoWon(); }
  };
  $('btn-nosale').onclick = () => cmd.noSale();
  $('btn-pass').onclick = () => cmd.passed();
  $('btn-reopen').onclick = () => {
    const to = reopenTarget(snap ?? null, reopenableOf(snap?.['reopenable']));
    if (!to) return;
    const hip = lotById(to.lotId)?.hipNumber ?? to.lotId;
    const r = prompt(to.from === 'HAMMERED' ? 'Reopen reason' : `Reopen ${saleWords().low} ${hip} (${to.from === 'PASSED' ? 'passed' : 'no sale'})? Bidding opens again where it stood. Reason`);
    if (r) cmd.reopen(to.lotId, r);
  };
  // ADR-70 cut 6c: the last bid, whatever its source, named in the question.
  // A ring older than that keeps the floor-bid correction.
  $('btn-undo').onclick = () => {
    const fix = bidCorrectionOffer(snap ?? null);
    if (fix.legacy) { const r = prompt('Reverse last floor bid - reason'); if (r) cmd.undoFloor(r); return; }
    const last = fix.retract;
    if (!last) return;
    const r = prompt(`Remove the last bid, ${usd(last.amount)} ${SOURCE_WORDS[last.source]}? The bid under it stands again.${last.source === 'FLOOR' ? '' : " Any max bid that bidder has on this lot ends too."} Reason`);
    if (r) cmd.retract(last.seq, r);
  };
  $('btn-reset').onclick = openReset;
  $('reset-go').onclick = sendReset;
  $('reset-cancel').onclick = closeReset;
  // Escape backs out with every bid where it was. Enter sends nothing: a
  // reset is pressed, never typed into.
  $('resetsheet').onkeydown = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeReset(); } };
  // ADR-70 cut 6d: the increment sheet, and taking a lot out of the ring.
  $('btn-inc').onclick = openIncrement;
  $('inc-use').onclick = useTypedStep;
  $('inc-table').onclick = () => chooseStep(null);
  $('inc-cancel').onclick = closeIncrement;
  $('inc-custom').onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); useTypedStep(); } };
  $('incsheet').onkeydown = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeIncrement(); } };
  $('btn-remove').onclick = () => {
    const hip = lotById(snap?.lotId)?.hipNumber ?? '?';
    const r = prompt(`Take ${saleWords().low} ${hip} out of the ring? It stays in the sale, unsold, and can be brought back. The run moves on to the next horse.\n\nReason (optional, kept on the record, never shown to bidders)`);
    if (r === null) return;
    cmd.removeFromRing(r.trim());
  };
  // ADR-70 cut 6e: a look at the max bids (recorded by the ring), and its sheet.
  $('btn-maxshow').onclick = () => { cmd.viewMaxBids(); };
  $('max-close').onclick = closeMaxBids;
  $('maxsheet').onkeydown = (e) => { if (e.key === 'Escape') { e.preventDefault(); closeMaxBids(); } };
  // ADR-70 cut 2: an equal-bid override is a recorded decision about money,
  // so it always asks why. The paddle is collected at SOLD like any floor bid.
  $('btn-award').onclick = () => {
    const offer = equalBidOffer(snap).award;
    if (!offer) return;
    const r = prompt(`Give the ${usd(offer.amount)} bid to the floor - reason (goes in the sale record)`);
    if (r === null) return;
    if (r.trim() === '') return say('a reason is required to override an equal bid', 'err');
    cmd.awardEqual(offer.bidSeq, r.trim());
  };
  $('btn-restore').onclick = () => {
    const offer = equalBidOffer(snap).restore;
    if (!offer) return;
    const r = prompt(`Give the ${usd(offer.amount)} bid back to the online bidder - reason (goes in the sale record)`);
    if (r === null) return;
    if (r.trim() === '') return say('a reason is required to override an equal bid', 'err');
    cmd.restoreEqual(offer.overrideSeq, r.trim());
  };
  $('btn-pause').onclick = () => (snap?.onlineStatus === 'PAUSED' ? cmd.resume() : cmd.pause());
  // ADR-60 cut 2.
  $('btn-accept').onclick = () => { const p = pendingBidOf(); if (p) cmd.acceptOnline(p.pendingBidId); };
  $('btn-decline').onclick = () => { const p = pendingBidOf(); if (p) cmd.declineOnline(p.pendingBidId); };
  $('btn-confirm').onclick = () => {
    const on = snap?.['confirmMode'] === true;
    // Once a lot has been in the ring the reducer wants a reason: this is a
    // recorded operational decision, not a preference.
    if (snap?.lotId) {
      const reason = prompt(`Switch confirm mode ${on ? 'OFF (internet bids will auto-accept)' : 'ON (internet bids will wait for you)'}. Reason for the record:`);
      if (!reason || !reason.trim()) return;
      cmd.setConfirmMode(!on, reason.trim());
    } else {
      cmd.setConfirmMode(!on);
    }
  };
  // ADR-42: a material announcement is an immutable ring event, not a text edit.
  // MATERIAL applies the sale's max-bid policy (default: invalidate by event).
  $('btn-announce').onclick = () => {
    if (saleFinalized) return;
    const text = prompt('Announcement text (all bidders will see this; it is logged permanently):');
    if (!text || !text.trim()) return;
    const material = confirm('Is this a MATERIAL change to the lot (cribber, papers, soundness, catalog correction)?\n\nOK = MATERIAL (applies the sale\'s max-bid policy)\nCancel = informational');
    const category = prompt('Category (VET / PAPERS / CATALOG_CORRECTION / SELLER_REP / TERMS / OTHER):', material ? 'VET' : 'OTHER') || 'OTHER';
    const toCurrent = snap?.lotId ? confirm(`Attach to the current lot (${lotById(snap.lotId)?.hipNumber ?? snap.lotId})?\n\nCancel = sale-wide announcement`) : false;
    // ADR-60 cut 3: a MATERIAL update blocks every bidder until they read it
    // (the sale's policy default); the clerk may waive that for this one.
    const requiresAcknowledgement = material ? confirm('Require every online bidder to acknowledge this update before they can bid again?\n\nOK = yes (recommended for disclosures)\nCancel = no, just announce it') : false;
    floor({ type: 'POST_ANNOUNCEMENT', lotId: toCurrent && snap?.lotId ? snap.lotId : null, text: text.trim(), severity: material ? 'MATERIAL' : 'INFO', category: category.trim().toUpperCase(), requiresAcknowledgement });
  };
  $('btn-endsale').onclick = () => {
    if (saleFinalized) return;
    if (snap && (snap.lotState === 'OPEN' || snap.lotState === 'FAIR_WARNING' || snap.lotState === 'HAMMERED_PENDING')) {
      return say('resolve the current lot (sold / no-sale / pass) before ending the sale', 'err');
    }
    if (!confirm('END SALE?\n\nThis FINALIZES the ring for settlement: no more bids, no more lots, and it CANNOT be undone. The office can then close & settle.\n\nEnd the sale now?')) return;
    cmd.endSale();
  };
  // Forward is the sale running: same rule as auto-advance, so tapping Next
  // never lands on a horse that is not coming.
  // ADR-70 cut 6d: with no lot in the ring after one was taken out, both
  // count from that one.
  const anchor = (): string | null => snap?.lotId ?? removedLotId;
  $('btn-next').onclick = () => { const a = anchor(); const n = a ? nextInRunOrder(ordered(), a) : null; if (n) cmd.setLot(n.id); };
  // Backward is the clerk going to FIX something, so it deliberately does not
  // skip sold or passed lots - those are exactly what you go back for. It
  // still skips scratches, which are never a useful destination.
  $('btn-prev').onclick = () => {
    const run = ordered();
    const i = run.findIndex((l) => l.id === anchor());
    if (i <= 0) return;
    const p = run.slice(0, i).reverse().find((l) => l.status !== 'scratched');
    if (p) cmd.setLot(p.id);
  };
  document.querySelectorAll<HTMLButtonElement>('.inc').forEach((b) => { b.onclick = () => { const ask = snap?.currentAsk ?? 0; const inc = incView().step; const step = b.dataset['step']; const v = step === '-1' ? Math.max(inc, ask - inc) : step === '0' ? ask : step === '1' ? ask + inc : step === '2' ? ask + inc * 2 : Number(prompt('Ask ($)', String(ask)) ?? 0); if (v > 0 && v !== ask) cmd.setAsk(v); }; });
  $('btn-signout').onclick = async () => { await session.signOut(); location.reload(); };

  // Results by default, raw engine activity a click away. The log is never
  // deleted - it just stops being the thing an operator stares at all sale.
  /**
   * Scratch: this horse is not coming.
   *
   * A catalog status change rather than a ring command, because a scratched
   * horse never enters the auction state machine at all. Once it is marked,
   * nextInRunOrder skips it for the rest of the sale, so the clerk never has
   * to remember. Confirmed, because it takes a horse out of the sale.
   */
  // Two taps, in place. A second tap on a button that has changed under your
  // finger is a real confirmation, and unlike a native dialog it does not stop
  // the ring or land somewhere the clerk has to aim at. It reverts itself after
  // a few seconds so a stray tap cannot leave a live scratch button armed.
  let scratchArmed: number | null = null;
  const disarmScratch = (): void => {
    if (scratchArmed !== null) window.clearTimeout(scratchArmed);
    scratchArmed = null;
    $('btn-scratch').classList.remove('armed');
    render();
  };
  $('btn-scratch').onclick = async () => {
    const l = lotById(snap?.lotId);
    if (!l) return;
    if (scratchArmed === null) {
      $('btn-scratch').textContent = `Confirm scratch of ${l.hipNumber}?`;
      $('btn-scratch').classList.add('armed');
      scratchArmed = window.setTimeout(disarmScratch, 4000);
      return;
    }
    disarmScratch();
    try {
      await api.call('patchLot', { params: { lotId: l.id }, body: { status: 'scratched', confirm: true }, identity: session.apiIdentity() });
      await refreshCatalog();
      say(`${saleWords().low} ${l.hipNumber} scratched`, 'ok');
      pushFeed(`✕ ${saleWords().low} ${l.hipNumber} scratched`);
      const next = nextInRunOrder(ordered(), l.id);
      if (next) cmd.setLot(next.id);
    } catch (e) {
      say(e instanceof ApiError ? `scratch failed: ${e.status} ${JSON.stringify(e.body)}` : String(e), 'err');
    }
  };

  // ADR-65: reverse the CURRENT lot's standing sale. The scratch button's own
  // two-tap pattern (armed in place, self-disarming), then a typed reason; the
  // ring records SALE_VOIDED and, because the lot is up, clears the board so
  // the horse can be re-offered at once.
  const armVoid = (id: 'btn-void' | 'btn-turnback', kind: 'VOID' | 'TURN_BACK'): void => {
    let armed: number | null = null;
    const disarm = (): void => {
      if (armed !== null) window.clearTimeout(armed);
      armed = null;
      $(id).classList.remove('armed');
      render();
    };
    $(id).onclick = () => {
      const l = lotById(snap?.lotId);
      const cs = (snap?.['currentSale'] as CurrentSale | null | undefined) ?? null;
      if (!l || !cs || cs.voidedSeq !== null) return;
      if (armed === null) {
        $(id).textContent = kind === 'VOID' ? `Confirm: void the sale of ${l.hipNumber}?` : `Confirm: turn back ${l.hipNumber}?`;
        $(id).classList.add('armed');
        armed = window.setTimeout(disarm, 4000);
        return;
      }
      disarm();
      askAndVoid(l.id, l.hipNumber, cs.soldSeq, kind);
    };
  };
  armVoid('btn-void', 'VOID');
  armVoid('btn-turnback', 'TURN_BACK');

  // A dropdown that will not go away is a dropdown sitting on top of the ring.
  // <details> only closes via its own summary, so clicking anywhere else - or
  // pressing Escape - closes it here. Covers the overflow menu and System.
  const closeMenus = (except?: Node): void => {
    document.querySelectorAll<HTMLDetailsElement>('details.menu[open]').forEach((d) => {
      if (!except || !d.contains(except)) d.open = false;
    });
  };
  document.addEventListener('click', (e) => closeMenus(e.target as Node));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenus(); });

  // ADR-70 cut 6a: three views of what happened - results, the bids on a
  // lot, and the ring's own activity lines.
  document.querySelectorAll<HTMLButtonElement>('#hist-views button').forEach((b) => {
    b.onclick = () => {
      const v = b.dataset['view'] as HistView;
      // Bids opens on the lot in the ring; a hip picked from the results
      // sets its own lot.
      if (v === 'bids') bidsLotId = null;
      setHistView(v);
    };
  });
  ($('bids-online') as HTMLInputElement).onchange = (e) => { onlineOnly = (e.target as HTMLInputElement).checked; renderBids(); };
  $('bids-back').onclick = () => { bidsLotId = null; renderBids(); };
  // Time in the ring counts up between events, and a read put off by the
  // floor between reads is taken when it is allowed.
  window.setInterval(() => { renderPace(); maybeReadHistory(); }, 1_000);
  renderResults();

  // ADR-70 cut 6g: the sale's checks, once a minute while the page is seen,
  // and at once when it comes back into view.
  renderChecks();
  maybeReadChecks();
  window.setInterval(maybeReadChecks, 5_000);
  document.addEventListener('visibilitychange', maybeReadChecks);

  // ---- paddle lookup (§11.2) ------------------------------------------------
  // Paddle, name, or phone - whatever the person in front of the clerk can
  // produce (brief section 26). Knowing WHICH field the system indexes on is
  // the software's problem, not theirs, so one box takes all three.
  // The rail earns its width. Slim while nothing is happening, wider the
  // moment a bidder is actually on screen, back to slim when the clerk clears
  // the box. On a tablet that difference is ~116px handed to the centre column,
  // which is where the auctioneer is looking.
  const railOpen = (open: boolean): void => { document.body.classList.toggle('rail-open', open); };

  const doPaddleLookup = (): void => {
    const raw = ($('paddle-in') as HTMLInputElement).value.trim();
    if (!raw) { setChildren($('paddle-result'), el('span', { class: 'no', text: 'paddle, name, or phone' })); railOpen(false); return; }
    railOpen(true);
    const found = findBidders(regs, raw);
    if (found.length === 0) { setChildren($('paddle-result'), el('span', { class: 'no', text: `no bidder matches "${raw}"` })); return; }
    setChildren($('paddle-result'), ...found.slice(0, 8).map((reg) => {
      const paddle = reg.livePaddle ?? reg.onlinePaddle;
      const which = reg.livePaddle != null ? 'live' : 'online';
      const tail = phoneTail(reg.bidderPhone);
      const cardBtn = el('button', { class: 'cardbtn', text: '🖨 Buyer card' });
      cardBtn.onclick = () => paddle != null && void printBuyerCard(reg.id, paddle);
      return el('div', { class: 'found' },
        el('span', { class: 'pad', text: paddle == null ? '-' : String(paddle) }),
        el('span', { class: 'meta' },
          el('b', { text: reg.bidderName ?? `account ${reg.accountId.slice(0, 8)}` }),
          ` · ${which} paddle`,
          tail ? ` · ***${tail}` : '',
          ' · ',
          el('b', { text: reg.approvalStatus }),
          reg.bidLimitCents ? ` · limit ${usd(reg.bidLimitCents / 100)}` : false,
          reg.approvalStatus === 'APPROVED' ? false : el('span', { class: 'no', text: ' - cannot bid' }),
        ),
        paddle == null ? false : cardBtn,
      );
    }), found.length > 8 ? el('span', { class: 'no', text: `+${found.length - 8} more - narrow the search` }) : false);
  };
  $('paddle-go').onclick = doPaddleLookup;
  // Emptying the box puts the rail back without needing a second tap.
  $('paddle-in').oninput = () => {
    if (($('paddle-in') as HTMLInputElement).value.trim()) return;
    setChildren($('paddle-result'), '');
    railOpen(false);
  };
  ($('paddle-in') as HTMLInputElement).addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.stopPropagation(); doPaddleLookup(); } });

  // ---- walk-up bidder registration (§11.4) ----------------------------------
  $('btn-walkup').onclick = async () => {
    if (!sale) return;
    const displayName = prompt('Walk-up bidder - name'); if (!displayName) return;
    const email = prompt('Email (optional)') ?? '';
    const paddleStr = prompt('Live paddle (blank = next free)') ?? '';
    const limitStr = prompt('Bid limit ($, optional)') ?? '';
    const body: { displayName: string; email?: string; livePaddle?: number; bidLimitCents?: number } = { displayName };
    if (email.trim()) body.email = email.trim();
    if (paddleStr.trim()) body.livePaddle = Number(paddleStr);
    if (limitStr.trim()) {
      const limit = bidLimitCents(limitStr);
      if (!limit.ok) { say(limit.error, 'err'); return; }
      body.bidLimitCents = limit.cents;
    }
    try {
      const preview = (await api.call('walkUp', { params: { saleId: sale.id }, body, identity: session.apiIdentity() })) as { preview?: boolean; wouldChange?: { livePaddle?: number } };
      if (!(preview && preview.preview)) { say('unexpected: no preview', 'err'); return; }
      const previewedPaddle = preview.wouldChange?.livePaddle;
      if (!confirm(`Register ${displayName} - paddle ${previewedPaddle}, APPROVED (terms accepted on paper)?`)) return;
      // PIN the paddle the clerk just approved into the confirm. Otherwise the
      // server would recompute "next free" and could commit a DIFFERENT paddle
      // than the one shown - if another clerk grabbed it meanwhile, the DB
      // uniqueness gives a clear 409 instead of a silent switch.
      const reg = (await api.call('walkUp', { params: { saleId: sale.id }, body: { ...body, ...(previewedPaddle !== undefined ? { livePaddle: previewedPaddle } : {}), confirm: true }, identity: session.apiIdentity() })) as { id: string; livePaddle: number };
      await refreshCatalog();
      say(`registered ${displayName} - paddle ${reg.livePaddle}`, 'ok');
      pushFeed(`＋ walk-up ${displayName} → paddle ${reg.livePaddle} (APPROVED)`);
      // The buyer just got their paddle - offer to print their Smart Buyer Card.
      if (confirm(`Print a buyer card for ${displayName} (paddle ${reg.livePaddle})?`)) void printBuyerCard(reg.id, reg.livePaddle);
    } catch (e) {
      say(e instanceof ApiError ? `walk-up failed: ${e.status} ${JSON.stringify(e.body)}` : String(e), 'err');
    }
  };

  // ---- on-the-fly lot (§11.3): create at the ring, then optionally load it --
  // C2b: one in-page dialog (lot-dialog.ts) replaced the chain of prompt() and
  // confirm() boxes. It collects and validates; the call, the ring, and the
  // console's own messages stay here.
  $('btn-addlot').onclick = () => {
    if (!sale) return;
    const saleId = sale.id;
    openLotDialog({
      words: saleWords(),
      kinds: KINDS,
      kind: lastLotKind,
      existing: () => saleLots.map((l) => ({ hipNumber: l.hipNumber, runOrder: l.runOrder, title: l.title })),
      loadSellers: loadSellerList,
      submit: async (lotBody, loadNow) => {
        const result = await addWalkInLot(
          {
            // ADR-29: a clerk is a human, so confirm passes the gate directly. The
            // dialog is the review (every field is on screen), so there is no
            // preview round trip and no second box.
            createLot: async (body) => (await api.call('createLot', { params: { saleId }, body, identity: session.apiIdentity() })) as { id: string; hipNumber: string },
            refreshCatalog,
            say,
            pushFeed,
            setLot: (lotId) => cmd.setLot(lotId),
            words: saleWords,
          },
          lotBody,
          loadNow,
          // A lot added at a ring's console lands in that ring (cut 5c).
          multiRing && myRing ? myRing.engineRingId : undefined,
        );
        if (result.ok) lastLotKind = lotBody.kind;
        return result;
      },
    });
  };

  // keyboard strip - global, unless typing in a field
  document.addEventListener('keydown', (e) => {
    const tgt = e.target as HTMLElement;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const r = keyReducer(keys, e.key, keyCtx());
    if (r.state !== keys || r.intent.type !== 'none') e.preventDefault();
    keys = r.state;
    switch (r.intent.type) {
      case 'floor_bid': cmd.floorBid(r.intent.amount, r.intent.paddle); say(`floor bid ${usd(r.intent.amount)} · paddle ${r.intent.paddle}`, 'ok'); break;
      case 'hammer': cmd.hammer(); break;
      case 'fair_warning': cmd.fairWarning(); break;
      case 'set_ask': cmd.setAsk(r.intent.ask); break;
      case 'error': say(r.intent.message, 'err'); break;
      default: break;
    }
    render();
  });
  render();
}

void main().catch((e) => { $('gate').textContent = e instanceof ApiError ? `API ${e.status}: ${JSON.stringify(e.body)}` : String(e); });


// Light/dark, remembered across the console (theme.ts).
initTheme();
